// SVG-based isometric illustrations for home cards.
//
// Goal: each card must instantly tell the user what the tool produces,
// MakerCase-style. We therefore stick to a consistent visual language:
//
//  • Same isometric projection for every BOX (so users can compare
//    Basic vs Grid vs Hinge vs Slide vs Polygon at a glance).
//  • Same plywood palette for cut-from-wood tools.
//  • Distinguishing FEATURE highlighted in cyan or red (finger joints,
//    grid dividers, hinge, sliding lid, kerf cuts, ...).
//  • Non-box tools (luminaire, maze, puzzle, vectorizer, image puzzle)
//    keep their own identity but use the same 260×200 viewBox so the
//    cards line up.
//
// All illustrations share the <Shell/> container which adds a soft drop
// shadow and a faint blueprint grid behind the artwork.
//
// ─── Admin override layer ────────────────────────────────────────────
// The admin panel can replace any tool's preview illustration with a
// custom JPEG/PNG/WebP stored in D1 (scope=tool, scope_key=slug).
// We fetch the public index ONCE per session and cache the slug set in
// a module-level variable so every card mount is O(1). When a slug is
// present in the override set we short-circuit the switch below and
// render `<img src='/api/media/tool/{slug}/image'>` instead.
import { useEffect, useState } from 'react';
import { apiBase } from '../lib/api';

let _toolOverrideCache = null;        // null = not loaded, Set<string> when loaded
let _toolOverrideLoading = null;      // pending Promise<Set<string>>
const _toolOverrideSubs = new Set();  // setState callbacks waiting for first load

function loadToolOverrides() {
  if (_toolOverrideCache) return Promise.resolve(_toolOverrideCache);
  if (_toolOverrideLoading) return _toolOverrideLoading;
  _toolOverrideLoading = fetch(`${apiBase}/api/media?scope=tool`, { credentials: 'omit' })
    .then((r) => (r.ok ? r.json() : { items: [] }))
    .then((data) => {
      const set = new Set((data?.items || []).map((it) => it.scope_key));
      _toolOverrideCache = set;
      _toolOverrideSubs.forEach((fn) => { try { fn(set); } catch {} });
      _toolOverrideSubs.clear();
      return set;
    })
    .catch(() => {
      _toolOverrideCache = new Set();
      _toolOverrideSubs.forEach((fn) => { try { fn(_toolOverrideCache); } catch {} });
      _toolOverrideSubs.clear();
      return _toolOverrideCache;
    });
  return _toolOverrideLoading;
}

function useToolOverride(slug) {
  const [has, setHas] = useState(() => (_toolOverrideCache ? _toolOverrideCache.has(slug) : false));
  useEffect(() => {
    if (!slug) return;
    if (_toolOverrideCache) {
      setHas(_toolOverrideCache.has(slug));
      return;
    }
    let mounted = true;
    const cb = (set) => { if (mounted) setHas(set.has(slug)); };
    _toolOverrideSubs.add(cb);
    loadToolOverrides();
    return () => { mounted = false; _toolOverrideSubs.delete(cb); };
  }, [slug]);
  return has;
}

// Allow other parts of the app (e.g. admin panel after upload) to
// invalidate the cache so the next render picks up the new override.
export function invalidateToolOverrideCache() {
  _toolOverrideCache = null;
  _toolOverrideLoading = null;
}

export default function BoxPreviewCard({ type }) {
  // Admin override short-circuit — if an admin uploaded a custom image
  // for this slug, render it ahead of the bundled illustration.
  const hasOverride = useToolOverride(type);
  if (hasOverride) {
    return <PhotoIllu src={`${apiBase}/api/media/tool/${encodeURIComponent(type)}/image`} alt={type} scale={1} />;
  }
  // Each tool gets a dedicated illustration so the user can identify
  // it from the picture alone. Tools without a real product photo use
  // an inline SVG that visually maps to what the tool produces (e.g.
  // jigsaw pieces for the puzzle, a maze pattern for the maze tool,
  // dotted halftone for photo-dots, etc.).
  if (type === 'polygon')          return <PolygonIllu />;
  if (type === 'curved')           return <CurvedIllu />;
  if (type === 'grid')             return <GridIllu />;
  if (type === 'cubby')            return <CubbyPhotoIllu />;
  if (type === 'cardboard')        return <CardboardIllu />;
  if (type === 'luminaire')        return <LuminaireIllu />;
  if (type === 'luminaire-shapes') return <LuminaireShapesIllu />;
  if (type === 'ballot')           return <BallotPhotoIllu />;
  if (type === 'photo-dots' || type === 'halftone') return <HalftonePhotoIllu />;
  if (type === 'maze')             return <MazeIllu />;
  if (type === 'puzzle')           return <PuzzleIllu />;
  if (type === 'image-puzzle')     return <ImagePuzzleIllu />;
  if (type === 'vectorize')        return <VectorizeIllu />;
  if (type === 'panel')            return <PanelIllu />;
  if (type === 'kerf')             return <KerfIllu />;
  if (type === 'focus')            return <FocusIllu />;
  if (type === 'hinge')            return <HingeIllu />;
  if (type === 'divider')          return <DividerIllu />;
  if (type === 'slide')            return <SlideIllu />;
  // ── Post-Focus-Jig tool illustrations ────────────────────────────────
  // Creative, function-evocative icons for the 11 tools added after the
  // laser focus gauge. Each one tries to capture the tool's PRODUCT in
  // a single glance, consistent with the plywood/blueprint visual
  // language used elsewhere on the home grid.
  if (type === 'gear-builder')     return <GearBuilderIllu />;
  if (type === 'bend-calculator')  return <BendCalculatorIllu />;
  if (type === 'l-bracket')        return <LBracketIllu />;
  if (type === 'enclosure')        return <EnclosureIllu />;
  if (type === 'rack-panel')       return <RackPanelIllu />;
  if (type === 'slice-stack')      return <SliceStackIllu />;
  if (type === 'fill-pattern')     return <FillPatternIllu />;
  if (type === 'connected-text')   return <ConnectedTextIllu />;
  if (type === 'single-line-text') return <SingleLineTextIllu />;
  if (type === 'stir-stick')       return <StirStickIllu />;
  if (type === 'neon-sign')        return <NeonSignIllu />;
  if (type === 'led-pixel-board')  return <LedPixelBoardIllu />;
  if (type === 'board-case')       return <BoardCaseIllu />;
  if (type === 'ai-dxf-gen')       return <AIDxfGenIllu />;
  if (type === 'polygon-3d')       return <Polygon3DIllu />;
  return <BasicIllu />;
}

/* Fallback photo illustrations for types whose dedicated SVG components
   were retired but whose preview images still ship in /preview/. */
function BallotPhotoIllu() {
  return <PhotoIllu src="/preview/ballot-box.webp" alt="Urna com tampa em compensado" scale={1} />;
}
function HalftonePhotoIllu() {
  return <PhotoIllu src="/preview/halftone.webp" alt="Padrão halftone: foto → pontos → painel em aço corten" scale={1} />;
}

/* ─── Shared palette ─────────────────────────────────────────────────── */
const WOOD       = '#E8B873';   // base plywood face
const WOOD_D     = '#B6843E';   // dark plywood face (front)
const WOOD_L     = '#FFE2B0';   // light plywood face (top)
const WOOD_DD    = '#8C5E25';   // edge / inner shadow
const EDGE       = '#1F2937';   // line-art outline
const ACCENT     = '#0891B2';   // cyan accent for highlighted features
const ACCENT_S   = '#06B6D4';   // softer cyan
const RED        = '#DC2626';   // fold / kerf line indicator

const KRAFT      = '#B97E4A';
const KRAFT_D    = '#8C5A2E';
const KRAFT_L    = '#D6A370';

/* ─── Container with subtle background grid ─────────────────────────── */
//
// The viewBox is intentionally LARGER than the original 260×200 artwork
// region (we use -10..270 × -10..210, i.e. 280×220 with a 10-unit margin
// on every side). This guarantees that any artwork slightly overshooting
// the original 260×200 region (e.g. SlideIllu reaches x=246 / y=195,
// VectorizeIllu reaches x=233, HingeIllu reaches y=195) stays fully
// visible inside the card without being clipped at the right or bottom
// edges. preserveAspectRatio defaults to "xMidYMid meet" so proportions
// are always preserved.
function Shell({ children, bg = false }) {
  return (
    <svg viewBox="-10 -10 280 220" className="w-full h-full"
         style={{ filter: 'drop-shadow(0 12px 22px rgba(0,0,0,0.16))' }}>
      {bg && (
        <defs>
          <pattern id="bpgrid" x="0" y="0" width="20" height="20" patternUnits="userSpaceOnUse">
            <path d="M 20 0 L 0 0 0 20" fill="none" stroke="#cfd8e3" strokeWidth="0.4" />
          </pattern>
        </defs>
      )}
      {bg && <rect x="-10" y="-10" width="280" height="220" fill="url(#bpgrid)" opacity="0.6" />}
      {children}
    </svg>
  );
}

/* ─── Reusable: isometric "open box" body ───────────────────────────── */
//
//  Returns the three faces (bottom-front rhombus, left wall, right wall)
//  of an isometric open box centred at (130, 95). Every BOX tool reuses
//  this so users can compare them directly.
//
//                  +----+    <- top opening (left/right walls visible)
//                 /|   /|
//                +-+--+ |
//                | |  | +
//                | +--|-+
//                |/   |/
//                +----+    <- bottom plate
//
function IsoBoxFrame({ showFingerJoints = false, showOpenLid = true, accentColor }) {
  const accent = accentColor || ACCENT;
  return (
    <>
      {/* bottom rhombus (visible through the open top) */}
      <polygon points="50,120 130,80 210,120 130,160"
               fill={WOOD_D} stroke={EDGE} strokeWidth="1.6" strokeLinejoin="round" />
      {/* left wall */}
      <polygon points="50,120 50,70 130,30 130,80"
               fill={WOOD_L} stroke={EDGE} strokeWidth="1.6" strokeLinejoin="round" />
      {/* right wall */}
      <polygon points="130,80 130,30 210,70 210,120"
               fill={WOOD} stroke={EDGE} strokeWidth="1.6" strokeLinejoin="round" />
      {/* Top opening rim (subtle) */}
      {showOpenLid && (
        <polyline points="50,70 130,30 210,70" fill="none"
                  stroke={EDGE} strokeWidth="0.9" opacity="0.55" />
      )}
      {/* finger joint hints — always shown on the visible vertical edge
          between the two walls, MakerCase-style */}
      {showFingerJoints && (
        <g stroke={accent} strokeWidth="1.6" fill="none" strokeLinecap="round">
          {/* central vertical edge: alternating notches */}
          <path d="M 130 80 L 130 30" />
          <path d="M 124 78 L 124 70 M 124 60 L 124 52 M 124 42 L 124 34" />
          <path d="M 136 78 L 136 70 M 136 60 L 136 52 M 136 42 L 136 34" />
          {/* horizontal teeth on left top edge */}
          <g>
            <path d="M 60 116 L 76 108" />
            <path d="M 84 104 L 100 96" />
            <path d="M 108 92 L 124 84" />
          </g>
          {/* horizontal teeth on right top edge */}
          <g>
            <path d="M 136 84 L 152 92" />
            <path d="M 160 96 L 176 104" />
            <path d="M 184 108 L 200 116" />
          </g>
        </g>
      )}
    </>
  );
}

/* ═══ BASIC BOX — photorealistic studio shot ═════════════════════════ */
//
// We swapped the synthetic SVG illustration for an actual product
// photo (an open laser-cut plywood box with finger joints). Vector
// SVGs were reading as cartoon — real plywood grain, kerf burn marks
// and the depth of the open lid sell the "this is what your tool
// produces" message far better. The image is a 512px WebP (~20 KB)
// served directly from /public/preview/ for zero JS overhead.
function BasicIllu() {
  // 0.92 scale + top alignment keeps the entire box visible without the
  // bottom edge slipping behind the title strip.
  return <PhotoIllu src="/preview/basic-box.webp" alt="Caixa básica em compensado" scale={1} />;
}

/* ─── Helper: photo-based card with the same Shell padding/grid ─────── */
//
// The WebP source images now have a transparent alpha channel (background
// removed via fal-bria-rmbg), so we drop the multiply blend hack used for
// the previous white-bg studio shots. The blueprint grid still sits behind
// the subject to keep visual coherence with the SVG cards.
function PhotoIllu({ src, alt, scale = 1 }) {
  // Centered (`items-center`) so each photo uses the available space
  // evenly. `object-contain` guarantees the FULL image is always
  // visible — no cropping, no overflow into the description below.
  // Drop-shadow was removed because the soft shadow visually leaked
  // into the description strip, making the photo look "cut off" at
  // the bottom even though the image itself was intact.
  return (
    <div className="w-full h-full relative">
      <svg viewBox="-10 -10 280 220" className="absolute inset-0 w-full h-full" preserveAspectRatio="none">
        <defs>
          <pattern id="bpgridPhoto" x="0" y="0" width="20" height="20" patternUnits="userSpaceOnUse">
            <path d="M 20 0 L 0 0 0 20" fill="none" stroke="#cfd8e3" strokeWidth="0.4" />
          </pattern>
        </defs>
        <rect x="-10" y="-10" width="280" height="220" fill="url(#bpgridPhoto)" opacity="0.6" />
      </svg>
      <div className="relative w-full h-full flex items-center justify-center">
        <img
          src={src}
          alt={alt}
          loading="lazy"
          className="object-contain"
          style={{
            width: `${scale * 100}%`,
            height: `${scale * 100}%`,
            maxWidth: '100%',
            maxHeight: '100%',
          }}
        />
      </div>
    </div>
  );
}

/* ═══ MAZE — top-down board with corridors and one path traced in cyan ═ */
function MazeIllu() {
  // Hand-laid 8×6 grid of walls. Corridors are the gaps; cyan path
  // traces a sample solution from entry (top-left) to exit (bottom-right).
  return (
    <Shell bg>
      {/* board background (light wood) */}
      <rect x="35" y="40" width="190" height="130" rx="6" ry="6"
            fill="#F5DDB0" stroke="#3F2E1A" strokeWidth="1.6" />
      {/* internal walls — drawn as a coherent maze layout */}
      <g stroke="#3F2E1A" strokeWidth="2.4" strokeLinecap="round" fill="none">
        {/* horizontal walls */}
        <path d="M 60 60 L 120 60" />
        <path d="M 140 60 L 200 60" />
        <path d="M 60 80 L 100 80" />
        <path d="M 120 80 L 160 80" />
        <path d="M 180 80 L 200 80" />
        <path d="M 80 100 L 120 100" />
        <path d="M 140 100 L 180 100" />
        <path d="M 60 120 L 100 120" />
        <path d="M 140 120 L 200 120" />
        <path d="M 80 140 L 140 140" />
        <path d="M 160 140 L 200 140" />
        {/* vertical walls */}
        <path d="M 80 40 L 80 60" />
        <path d="M 120 60 L 120 80" />
        <path d="M 100 80 L 100 100" />
        <path d="M 160 60 L 160 100" />
        <path d="M 140 100 L 140 140" />
        <path d="M 180 100 L 180 140" />
        <path d="M 60 100 L 60 140" />
        <path d="M 100 120 L 100 170" />
        <path d="M 200 60 L 200 120" />
      </g>
      {/* entry & exit notches */}
      <rect x="30" y="50" width="8" height="14" fill="#F5DDB0" stroke="#3F2E1A" strokeWidth="1.4" />
      <rect x="222" y="148" width="8" height="14" fill="#F5DDB0" stroke="#3F2E1A" strokeWidth="1.4" />
      {/* solution path (cyan) — gives the user instant "this is a maze" cue */}
      <path d="M 38 57 L 70 57 L 70 90 L 90 90 L 90 110 L 130 110 L 130 130 L 170 130 L 170 155 L 222 155"
            fill="none" stroke="#0891B2" strokeWidth="2.8" strokeLinecap="round" strokeLinejoin="round"
            opacity="0.9" />
      {/* marble at the exit */}
      <circle cx="220" cy="155" r="4" fill="#DC2626" stroke="#3F2E1A" strokeWidth="0.8" />
    </Shell>
  );
}

/* ═══ PUZZLE — classic jigsaw 4-piece arrangement with knobs & sockets ═ */
function PuzzleIllu() {
  // Four interlocking pieces drawn with the standard jigsaw silhouette.
  // Different fill tints make the boundaries pop without an extra outline.
  const PIECE_A = '#F2C97A';
  const PIECE_B = '#E8B873';
  const PIECE_C = '#D9A058';
  const PIECE_D = '#C58944';
  const STROKE  = '#3F2E1A';
  // Single-piece SVG path generator: rectangle 60x60 with a knob on
  // one side and a socket on the opposite side. We draw 4 pieces,
  // rotated/positioned so the knobs interlock visually.
  return (
    <Shell bg>
      <g stroke={STROKE} strokeWidth="1.6" strokeLinejoin="round">
        {/* TOP-LEFT piece — knob on right & bottom */}
        <path d="M 70 50
                 L 130 50
                 L 130 75 c 8 -6 18 -6 18 4 c 0 10 -10 10 -18 4
                 L 130 110
                 L 100 110 c 6 8 6 18 -4 18 c -10 0 -10 -10 -4 -18
                 L 70 110 Z"
              fill={PIECE_A} />
        {/* TOP-RIGHT piece — socket on left, bottom knob */}
        <path d="M 130 50
                 L 190 50
                 L 190 110
                 L 160 110 c 6 8 6 18 -4 18 c -10 0 -10 -10 -4 -18
                 L 130 110
                 L 130 83 c -8 6 -18 6 -18 -4 c 0 -10 10 -10 18 -4 Z"
              fill={PIECE_B} />
        {/* BOTTOM-LEFT piece — socket on top, knob on right */}
        <path d="M 70 110
                 L 100 110 c 6 -8 6 -18 -4 -18 c -10 0 -10 10 -4 18
                 L 130 110
                 L 130 135 c 8 -6 18 -6 18 4 c 0 10 -10 10 -18 4
                 L 130 170
                 L 70 170 Z"
              fill={PIECE_C} />
        {/* BOTTOM-RIGHT piece — sockets on top & left */}
        <path d="M 130 110
                 L 160 110 c 6 -8 6 -18 -4 -18 c -10 0 -10 10 -4 18
                 L 190 110
                 L 190 170
                 L 130 170
                 L 130 143 c -8 6 -18 6 -18 -4 c 0 -10 10 -10 18 -4 Z"
              fill={PIECE_D} />
      </g>
    </Shell>
  );
}

/* ═══ IMAGE-PUZZLE — photo torn into jigsaw pieces ═══════════════════ */
function ImagePuzzleIllu() {
  // A photo divided into a 3×2 grid of jigsaw pieces — the same gradient
  // sky/landscape behind, with cut lines following classic puzzle knobs.
  return (
    <Shell bg>
      <defs>
        <linearGradient id="imgPuzzleSky" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%"   stopColor="#FCD68A" />
          <stop offset="60%"  stopColor="#E59B5B" />
          <stop offset="100%" stopColor="#C25D3D" />
        </linearGradient>
        <clipPath id="imgPuzzleClip">
          <rect x="50" y="50" width="160" height="120" rx="4" />
        </clipPath>
      </defs>
      {/* photo background (sunset gradient) */}
      <rect x="50" y="50" width="160" height="120" rx="4"
            fill="url(#imgPuzzleSky)" stroke="#3F2E1A" strokeWidth="1.6" />
      {/* simple landscape silhouette inside the photo */}
      <g clipPath="url(#imgPuzzleClip)">
        {/* sun */}
        <circle cx="160" cy="105" r="14" fill="#FFE07A" opacity="0.95" />
        {/* mountains */}
        <path d="M 50 150 L 80 120 L 100 140 L 130 110 L 160 145 L 190 125 L 210 150 L 210 170 L 50 170 Z"
              fill="#5C3A2E" opacity="0.85" />
        {/* foreground */}
        <rect x="50" y="155" width="160" height="15" fill="#3D2820" />
      </g>
      {/* jigsaw cut lines (3 cols × 2 rows) drawn ON TOP of the photo */}
      <g stroke="#FFFFFF" strokeWidth="1.8" fill="none" strokeLinecap="round">
        {/* vertical cuts with knobs */}
        <path d="M 103 50
                 L 103 100 c 6 -4 14 -4 14 4 c 0 8 -8 8 -14 4
                 L 103 120 c 6 -4 14 -4 14 4 c 0 8 -8 8 -14 4
                 L 103 170" />
        <path d="M 157 50
                 L 157 100 c -6 -4 -14 -4 -14 4 c 0 8 8 8 14 4
                 L 157 120 c -6 -4 -14 -4 -14 4 c 0 8 8 8 14 4
                 L 157 170" />
        {/* horizontal cut with knob */}
        <path d="M 50 110
                 L 80 110 c -4 6 -4 14 4 14 c 8 0 8 -8 4 -14
                 L 130 110 c -4 6 -4 14 4 14 c 8 0 8 -8 4 -14
                 L 180 110 c -4 6 -4 14 4 14 c 8 0 8 -8 4 -14
                 L 210 110" />
      </g>
      {/* dark outline overlay so cut lines read clearly */}
      <g stroke="#3F2E1A" strokeWidth="0.6" fill="none" strokeLinecap="round" opacity="0.5">
        <path d="M 103 50 L 103 170 M 157 50 L 157 170 M 50 110 L 210 110" />
      </g>
    </Shell>
  );
}

/* ═══ VECTORIZE — raster image becomes vector outlines ═══════════════ */
function VectorizeIllu() {
  // Left half: blurry pixelated bitmap (a face-like blob). Right half:
  // crisp vector outline of the same blob, with anchor points visible.
  // Arrow in the middle implies the conversion.
  return (
    <Shell bg>
      {/* LEFT — pixelated raster (each "pixel" is a small square) */}
      <g>
        <rect x="40" y="55" width="90" height="110" rx="4" fill="#FFFFFF" stroke="#3F2E1A" strokeWidth="1.4" />
        {/* simulate pixels with a coarse 9×11 grid of soft tone squares */}
        {(() => {
          const pixels = [];
          const palette = ['#3F2E1A', '#7C5A3E', '#B6843E', '#E8B873', '#FFE2B0'];
          // Hard-coded pixel "image" of a smiley face for clarity
          const map = [
            '00111100',
            '01222210',
            '12233221',
            '12343321',
            '12343321',
            '12233221',
            '12300321',
            '12303321',
            '12233221',
            '01222210',
            '00111100',
          ];
          for (let r = 0; r < map.length; r++) {
            for (let c = 0; c < map[r].length; c++) {
              const v = parseInt(map[r][c], 10);
              pixels.push(
                <rect
                  key={`px${r}-${c}`}
                  x={42 + c * 10.5}
                  y={57 + r * 9.5}
                  width="10.5"
                  height="9.5"
                  fill={palette[v]}
                  opacity={v === 0 ? 0 : 1}
                />
              );
            }
          }
          return pixels;
        })()}
      </g>

      {/* ARROW — raster → vector */}
      <g stroke="#0891B2" strokeWidth="2.4" fill="none" strokeLinecap="round" strokeLinejoin="round">
        <path d="M 134 110 L 154 110" />
        <path d="M 148 104 L 154 110 L 148 116" />
      </g>

      {/* RIGHT — clean vector silhouette of the same face */}
      <g>
        <rect x="160" y="55" width="90" height="110" rx="4" fill="#FFFFFF" stroke="#3F2E1A" strokeWidth="1.4" />
        {/* face outline as a smooth Bezier shape */}
        <path d="M 205 70
                 C 230 70 240 95 240 115
                 C 240 140 225 155 205 155
                 C 185 155 170 140 170 115
                 C 170 95 180 70 205 70 Z"
              fill="none" stroke="#0891B2" strokeWidth="2.2" />
        {/* eyes */}
        <circle cx="192" cy="105" r="3.5" fill="none" stroke="#0891B2" strokeWidth="2" />
        <circle cx="218" cy="105" r="3.5" fill="none" stroke="#0891B2" strokeWidth="2" />
        {/* smile */}
        <path d="M 188 130 Q 205 144 222 130" fill="none" stroke="#0891B2" strokeWidth="2" strokeLinecap="round" />
        {/* anchor points (Bezier handles) — the unmistakable "vector" cue */}
        <g fill="#FFFFFF" stroke="#0891B2" strokeWidth="1.4">
          <rect x="203" y="68" width="4" height="4" />
          <rect x="238" y="113" width="4" height="4" />
          <rect x="203" y="153" width="4" height="4" />
          <rect x="168" y="113" width="4" height="4" />
        </g>
      </g>
    </Shell>
  );
}

/* ═══ PANEL — decorative geometric panel (mandala-style lattice) ═════ */
function PanelIllu() {
  // Square panel with concentric geometric pattern — radial mandala +
  // border lattice. Reads instantly as "decorative architectural panel".
  return (
    <Shell bg>
      {/* panel frame */}
      <rect x="55" y="40" width="150" height="130" rx="4"
            fill="#E8B873" stroke="#3F2E1A" strokeWidth="1.8" />
      {/* central mandala — 8-pointed star + concentric rings, all CUT-OUT
          (rendered with darker fill to read as holes) */}
      <g fill="#3F2E1A" opacity="0.85">
        {/* outer ring of petals */}
        {Array.from({ length: 12 }).map((_, i) => {
          const ang = (i * Math.PI * 2) / 12;
          const cx = 130 + Math.cos(ang) * 50;
          const cy = 105 + Math.sin(ang) * 50;
          return <circle key={`p${i}`} cx={cx} cy={cy} r="5" />;
        })}
        {/* 8-pointed star (two overlapping squares rotated 45°) */}
        <path d="M 130 70 L 140 95 L 165 105 L 140 115 L 130 140 L 120 115 L 95 105 L 120 95 Z" />
        <path d="M 130 78 L 138 100 L 158 105 L 138 110 L 130 132 L 122 110 L 102 105 L 122 100 Z"
              fill="#E8B873" />
        {/* central hole */}
        <circle cx="130" cy="105" r="6" />
      </g>
      {/* corner ornaments */}
      <g fill="#3F2E1A" opacity="0.7">
        <circle cx="68" cy="53" r="3" />
        <circle cx="192" cy="53" r="3" />
        <circle cx="68" cy="157" r="3" />
        <circle cx="192" cy="157" r="3" />
      </g>
      {/* border inner-line so the panel reads framed */}
      <rect x="62" y="47" width="136" height="116" rx="2"
            fill="none" stroke="#3F2E1A" strokeWidth="0.8" opacity="0.6" />
    </Shell>
  );
}

/* ═══ KERF — calibration test plate with stepped slots ═══════════════ */
function KerfIllu() {
  // Small wooden tester with a column of horizontal slots in fine
  // increments + a finger-joint comb on the right. Each slot labelled
  // with its width to evoke the calibration purpose.
  return (
    <Shell bg>
      {/* plate */}
      <rect x="55" y="40" width="150" height="130" rx="3"
            fill="#E8B873" stroke="#3F2E1A" strokeWidth="1.6" />
      {/* stepped slot column (kerf width tester) */}
      <g fill="#3F2E1A">
        {[0, 1, 2, 3, 4, 5, 6].map(i => (
          <rect key={`s${i}`} x="70" y={55 + i * 14} width={50 + i * 1.5}
                height={2 + i * 0.4} rx="0.5" />
        ))}
      </g>
      {/* labels */}
      <g fontFamily="JetBrains Mono, monospace" fontSize="6.5" fill="#3F2E1A" opacity="0.85">
        {[0.10, 0.12, 0.14, 0.16, 0.18, 0.20, 0.22].map((v, i) => (
          <text key={`l${i}`} x="125" y={60 + i * 14} textAnchor="start">{v.toFixed(2)} mm</text>
        ))}
      </g>
      {/* finger-joint comb (right side) */}
      <g fill="#3F2E1A">
        {Array.from({ length: 6 }).map((_, i) => (
          <rect key={`t${i}`} x={170 + (i % 2) * 6} y={55 + i * 18} width="14" height="14" />
        ))}
      </g>
      {/* outline of the comb area */}
      <rect x="168" y="50" width="30" height="115" fill="none"
            stroke="#3F2E1A" strokeWidth="0.7" opacity="0.5" strokeDasharray="2,2" />
    </Shell>
  );
}

/* ═══ FOCUS GAUGE — staircase ramp with Z-offset labels ══════════════ */
function FocusIllu() {
  // Side-view staircase: each step rises and is labelled with a Z
  // offset. A red laser dot marks one step (the "best focus" finding).
  return (
    <Shell bg>
      {/* baseline plate */}
      <rect x="40" y="150" width="180" height="14"
            fill="#E8B873" stroke="#3F2E1A" strokeWidth="1.6" />
      {/* staircase (8 ascending steps) */}
      <g fill="#E8B873" stroke="#3F2E1A" strokeWidth="1.4" strokeLinejoin="round">
        {Array.from({ length: 8 }).map((_, i) => {
          const x = 50 + i * 20;
          const w = 22;
          const h = 8 + i * 11;
          return <rect key={`step${i}`} x={x} y={150 - h} width={w} height={h} />;
        })}
      </g>
      {/* Z labels above each step */}
      <g fontFamily="JetBrains Mono, monospace" fontSize="6.5" fill="#3F2E1A" opacity="0.9">
        {Array.from({ length: 8 }).map((_, i) => (
          <text key={`z${i}`} x={61 + i * 20} y={150 - (8 + i * 11) - 3}
                textAnchor="middle">{(i + 1)}</text>
        ))}
      </g>
      {/* laser beam to the "best focus" step (4th) */}
      <g>
        <line x1="120" y1="35" x2="120" y2="86" stroke="#DC2626" strokeWidth="2" strokeLinecap="round" opacity="0.85" />
        <circle cx="120" cy="86" r="4" fill="#DC2626" opacity="0.9" />
        <circle cx="120" cy="86" r="8" fill="none" stroke="#DC2626" strokeWidth="1" opacity="0.5" />
      </g>
      {/* arrow indicating "best focus" */}
      <g stroke="#0891B2" strokeWidth="1.6" fill="none" strokeLinecap="round">
        <path d="M 145 50 L 130 65" />
        <path d="M 130 65 L 137 65 M 130 65 L 130 58" />
      </g>
      <text x="148" y="48" fontFamily="JetBrains Mono, monospace" fontSize="7" fill="#0891B2">
        best Z
      </text>
    </Shell>
  );
}

/* ═══ HINGE BOX — closed wooden box with curved living hinge ═════════ */
function HingeIllu() {
  // Wooden box where the curved face is the living hinge (kerf grooves
  // visible). Same plywood palette as the other boxes.
  const FACE = '#D9B27E';
  const TOP  = '#F2DDB0';
  const SHADOW = '#A87B3F';
  const OUTLINE = '#3F2E1A';
  return (
    <Shell bg>
      {/* main body */}
      <path d="M 60 60
               L 200 60
               L 200 90 Q 200 130 200 150
               L 200 160
               L 60 160
               L 60 90 Q 60 75 75 60 Z"
            fill={FACE} stroke={OUTLINE} strokeWidth="1.6" strokeLinejoin="round" />
      {/* lid (top) */}
      <path d="M 60 60 Q 60 50 70 50 L 200 50 L 200 60 L 60 60 Z"
            fill={TOP} stroke={OUTLINE} strokeWidth="1.4" strokeLinejoin="round" />
      {/* curved kerf hinge — vertical fine grooves on the rounded corner */}
      <g stroke={SHADOW} strokeWidth="1" opacity="0.85" strokeLinecap="round" fill="none">
        {Array.from({ length: 14 }).map((_, i) => {
          const t = i / 13;
          // points along the curved corner
          const x1 = 60 + t * 18;
          const y1 = 90 - t * 30;
          const x2 = x1 - 2 + t * 2;
          const y2 = y1 - 6;
          return <line key={`k${i}`} x1={x1} y1={y1} x2={x2} y2={y2} />;
        })}
        {/* additional kerf lines along the body for visibility */}
        {Array.from({ length: 12 }).map((_, i) => (
          <line key={`bk${i}`} x1={70 + i * 5} y1={66} x2={70 + i * 5} y2={76} />
        ))}
      </g>
      {/* finger-joint hint on the right edge */}
      <g fill={SHADOW}>
        {[0, 1, 2, 3, 4].map(i => (
          <rect key={`fj${i}`} x={196} y={75 + i * 17} width={6} height={9} stroke={OUTLINE} strokeWidth="0.6" />
        ))}
      </g>
      {/* small front handle */}
      <rect x="125" y="105" width="10" height="6" rx="1.5"
            fill={SHADOW} stroke={OUTLINE} strokeWidth="0.8" />
    </Shell>
  );
}

/* ═══ DIVIDER — open box with custom (non-grid) interior layout ══════ */
function DividerIllu() {
  // Top-down view of a box with an irregular divider layout (one large
  // bay + 4 smaller bays of varying sizes). Differentiates from the
  // grid divider by having NON-uniform compartments.
  return (
    <Shell bg>
      {/* outer box */}
      <rect x="55" y="40" width="150" height="130" rx="3"
            fill="#FAEAC6" stroke="#3F2E1A" strokeWidth="1.8" />
      <rect x="62" y="47" width="136" height="116" rx="2"
            fill="#E8B873" stroke="#3F2E1A" strokeWidth="0.6" opacity="0.6" />
      {/* internal dividers (irregular layout) */}
      <g stroke="#3F2E1A" strokeWidth="2.4" strokeLinecap="square" fill="none">
        {/* main vertical divider */}
        <line x1="130" y1="47" x2="130" y2="163" />
        {/* horizontal dividers in the right column */}
        <line x1="130" y1="85" x2="198" y2="85" />
        <line x1="130" y1="125" x2="198" y2="85" /> {/* diagonal slot */}
        <line x1="130" y1="125" x2="198" y2="125" />
        {/* small divider in the left column */}
        <line x1="62" y1="115" x2="100" y2="115" />
      </g>
      {/* "items" to suggest compartments are usable */}
      <g fill="#3F2E1A" opacity="0.4">
        <circle cx="90" cy="80" r="9" />
        <rect x="76" y="135" width="18" height="20" rx="2" />
        <circle cx="160" cy="65" r="6" />
        <rect x="155" y="100" width="30" height="14" rx="1.5" />
      </g>
    </Shell>
  );
}

/* ═══ SLIDE — box with a sliding lid pulled half-open ════════════════ */
function SlideIllu() {
  // Isometric view of a box with the lid pulled out partially toward
  // the viewer, exposing one side of the cavity. The sliding rail is
  // visible on top.
  const FACE = '#D9B27E';
  const TOP  = '#F2DDB0';
  const FLOOR = '#FAEAC6';
  const SHADOW = '#A87B3F';
  const OUTLINE = '#3F2E1A';
  return (
    <Shell bg>
      {/* box body — front, top-rim, side */}
      <polygon points="55,90 165,90 165,160 55,160"
               fill={FACE} stroke={OUTLINE} strokeWidth="1.6" strokeLinejoin="round" />
      <polygon points="55,90 165,90 195,72 85,72"
               fill={TOP} stroke={OUTLINE} strokeWidth="1.4" strokeLinejoin="round" />
      <polygon points="165,90 195,72 195,142 165,160"
               fill={SHADOW} stroke={OUTLINE} strokeWidth="1.4" strokeLinejoin="round" />
      {/* inner cavity — visible because the lid is partially open */}
      <polygon points="65,98 130,98 130,150 65,150"
               fill={FLOOR} stroke={OUTLINE} strokeWidth="0.9" opacity="0.95" />
      {/* sliding lid (pulled half-out toward upper-right) */}
      <polygon points="120,72 230,72 230,90 120,90"
               fill={TOP} stroke={OUTLINE} strokeWidth="1.4" strokeLinejoin="round" />
      <polygon points="230,72 245,62 135,62 120,72"
               fill={FACE} stroke={OUTLINE} strokeWidth="1.4" strokeLinejoin="round" />
      <polygon points="230,72 245,62 245,80 230,90"
               fill={SHADOW} stroke={OUTLINE} strokeWidth="1.4" strokeLinejoin="round" />
      {/* sliding rail/groove on the box top rim — shown as 2 parallel lines */}
      <g stroke={SHADOW} strokeWidth="1" opacity="0.8">
        <line x1="85" y1="76" x2="195" y2="76" strokeDasharray="3,2" />
        <line x1="85" y1="86" x2="195" y2="86" strokeDasharray="3,2" />
      </g>
      {/* small finger pull on the sliding lid */}
      <circle cx="200" cy="78" r="3" fill={SHADOW} stroke={OUTLINE} strokeWidth="0.8" />
      {/* arrow indicating slide direction */}
      <g stroke="#0891B2" strokeWidth="1.6" fill="none" strokeLinecap="round" opacity="0.85">
        <path d="M 165 50 L 195 35" />
        <path d="M 195 35 L 188 36 M 195 35 L 193 42" />
      </g>
    </Shell>
  );
}

/* ═══ BASIC BOX — legacy SVG (kept for reference, not exported) ════════ */
function BasicIlluSvg() {
  // Plywood palette tuned to the reference (light maple/birch).
  const FACE_FRONT = '#D9B27E';   // front face (slightly shadowed)
  const FACE_SIDE  = '#E8C896';   // side face (lit)
  const FACE_TOP   = '#F2DDB0';   // top edge / rim (most lit)
  const FLOOR      = '#FAEAC6';   // inner floor seen through opening
  const SHADOW     = '#A87B3F';   // inner-shadow under the rim
  const OUTLINE    = '#3F2E1A';   // dark outline (was EDGE)

  // Reusable: a row of finger-joint teeth as a clip-style pattern on a
  // bottom edge. Each tooth is a small darker rectangle that sits flush
  // with the panel face — exactly how a finger-joint reads from outside.
  const ToothRow = ({ x, y, count = 6, w = 7, h = 5, gap = 6, fill = SHADOW }) => (
    <g>
      {Array.from({ length: count }).map((_, i) => (
        <rect
          key={i}
          x={x + i * (w + gap)}
          y={y}
          width={w}
          height={h}
          fill={fill}
          stroke={OUTLINE}
          strokeWidth="0.7"
        />
      ))}
    </g>
  );

  return (
    <Shell bg>
      {/* ── Back wall (peeking above the front edge) ───────────────────── */}
      <polygon
        points="62,72 198,72 198,108 62,108"
        fill={FACE_SIDE}
        stroke={OUTLINE}
        strokeWidth="1.4"
        strokeLinejoin="round"
      />

      {/* ── Inner floor (cream rectangle seen through the open top) ────── */}
      <polygon
        points="68,108 192,108 192,140 68,140"
        fill={FLOOR}
        stroke={OUTLINE}
        strokeWidth="1.2"
        strokeLinejoin="round"
        opacity="0.95"
      />
      {/* subtle inner-rim shadow just under the back wall top */}
      <polygon
        points="68,108 192,108 188,114 72,114"
        fill={SHADOW}
        opacity="0.35"
      />

      {/* ── Right side wall (3/4 perspective) ──────────────────────────── */}
      <polygon
        points="198,72 218,80 218,148 198,140"
        fill={FACE_SIDE}
        stroke={OUTLINE}
        strokeWidth="1.4"
        strokeLinejoin="round"
      />

      {/* ── Left side wall (front-facing, visible front face) ──────────── */}
      <polygon
        points="42,80 62,72 62,140 42,148"
        fill={FACE_FRONT}
        stroke={OUTLINE}
        strokeWidth="1.4"
        strokeLinejoin="round"
      />

      {/* ── Front panel ────────────────────────────────────────────────── */}
      <polygon
        points="62,108 198,108 198,140 62,140"
        fill={FACE_FRONT}
        stroke={OUTLINE}
        strokeWidth="1.4"
        strokeLinejoin="round"
      />

      {/* ── Top rim (the visible "open top" plywood edges) ─────────────── */}
      {/* back rim */}
      <polygon
        points="62,72 198,72 200,69 60,69"
        fill={FACE_TOP}
        stroke={OUTLINE}
        strokeWidth="1.0"
      />
      {/* left rim (slanted) */}
      <polygon
        points="42,80 62,72 60,69 40,77"
        fill={FACE_TOP}
        stroke={OUTLINE}
        strokeWidth="1.0"
      />
      {/* right rim (slanted) */}
      <polygon
        points="198,72 218,80 220,77 200,69"
        fill={FACE_TOP}
        stroke={OUTLINE}
        strokeWidth="1.0"
      />

      {/* ══ Finger-joint TEETH — the defining feature of the basic box ══ */}

      {/* Bottom-front teeth (across the front bottom edge) */}
      <ToothRow x={66} y={140} count={9} w={10} h={6} gap={4} />

      {/* Front-left vertical teeth (front face, left edge) */}
      <g>
        {[0, 1, 2, 3, 4].map(i => (
          <rect
            key={i}
            x={62}
            y={114 + i * 5.2}
            width={6}
            height={3.6}
            fill={SHADOW}
            stroke={OUTLINE}
            strokeWidth="0.6"
          />
        ))}
      </g>

      {/* Front-right vertical teeth (front face, right edge) */}
      <g>
        {[0, 1, 2, 3, 4].map(i => (
          <rect
            key={i}
            x={192}
            y={114 + i * 5.2}
            width={6}
            height={3.6}
            fill={SHADOW}
            stroke={OUTLINE}
            strokeWidth="0.6"
          />
        ))}
      </g>

      {/* Right-side bottom teeth (right face, along the bottom) */}
      <g>
        {[0, 1, 2, 3].map(i => (
          <rect
            key={i}
            x={200 + i * 4.6}
            y={138 - i * 0.8}
            width={3.4}
            height={4}
            fill={SHADOW}
            stroke={OUTLINE}
            strokeWidth="0.6"
          />
        ))}
      </g>

      {/* Left-side bottom teeth (left face, along the bottom) */}
      <g>
        {[0, 1, 2, 3].map(i => (
          <rect
            key={i}
            x={45 + i * 4.6}
            y={143.5 - i * 0.8}
            width={3.4}
            height={4}
            fill={SHADOW}
            stroke={OUTLINE}
            strokeWidth="0.6"
          />
        ))}
      </g>

      {/* dimension hint */}
      <g fontFamily="JetBrains Mono, monospace" fontSize="9" fill={EDGE} opacity="0.6">
        <text x="130" y="178" textAnchor="middle">W × D × H</text>
      </g>
    </Shell>
  );
}

/* ═══ GRID DIVIDER BOX — photorealistic 3D render ═════════════════════ */
//
// Replaced the synthetic SVG illustration with a real 3D render of the
// box with N×M interlocking dividers (2×2 grid in the reference shot).
// The render shows the open-top wooden box with vertical and horizontal
// dividers crossing at half-height — the unmistakable signature of
// this template. The image is rendered with a soft shadow and a 4:3
// aspect, so it fits the same preview frame as the other photo cards.
function GridIllu() {
  return <PhotoIllu src="/preview/grid-divider-box.webp" alt="Caixa com divisórias internas em grade 3×4" scale={1} />;
}

/* ═══ CUBBY SHELF — photo illustration ════════════════════════════════════
 * Open shelving grid (rows × cols of cubbies). The photo shows a 9×6
 * plywood shelf populated with little toy cars / figures, perfectly
 * conveying the "estante nichos" use case: many small open compartments
 * for displaying collections.
 * ════════════════════════════════════════════════════════════════════════ */
function CubbyPhotoIllu() {
  return <PhotoIllu src="/preview/cubby-shelf.webp" alt="Estante de nichos em compensado exibindo coleção de miniaturas" scale={1} />;
}

/* ═══ GRID DIVIDER BOX — legacy SVG (kept for reference, not exported) ═ */
function GridIlluSvg() {
  // Bottom-rhombus origin and basis vectors for an isometric box
  // (must mirror IsoBoxFrame's footprint).
  const O = { x: 50, y: 120 };
  const U = { x: 80, y: -40 };  // toward back-left corner (130, 80)
  const V = { x: 80, y:  40 };  // toward front-right corner (130, 160)
  const wallH = 50;             // wall height in screen px (top is 50px above floor)

  const P = (u, v) => ({ x: O.x + U.x * u + V.x * v, y: O.y + U.y * u + V.y * v });
  const top = (p) => ({ x: p.x, y: p.y - wallH });

  const cols = 3; // dividers split the box into 3 columns (→ 2 internal walls along V)
  const rows = 2; // and 2 rows (→ 1 internal wall along U)

  // Internal walls parallel to the LEFT outer wall (constant v, spans u 0→1)
  const longWalls = [];
  for (let i = 1; i < rows; i++) {
    const v = i / rows;
    const a = P(0, v); const b = P(1, v);
    longWalls.push({ a, b, top_a: top(a), top_b: top(b) });
  }
  // Internal walls parallel to the RIGHT outer wall (constant u, spans v 0→1)
  const shortWalls = [];
  for (let j = 1; j < cols; j++) {
    const u = j / cols;
    const a = P(u, 0); const b = P(u, 1);
    shortWalls.push({ a, b, top_a: top(a), top_b: top(b) });
  }

  const fmt = (p) => `${p.x.toFixed(1)},${p.y.toFixed(1)}`;

  return (
    <Shell bg>
      <IsoBoxFrame showFingerJoints={false} />

      {/* ── Internal divider walls ─────────────────────────────────── */}
      {/* Long walls (parallel to left wall) — drawn first so short
          walls overlap them where they cross.                         */}
      {longWalls.map((w, i) => (
        <g key={`L${i}`}>
          {/* near (visible) face of the wall                           */}
          <polygon
            points={`${fmt(w.a)} ${fmt(w.top_a)} ${fmt(w.top_b)} ${fmt(w.b)}`}
            fill={WOOD_L} stroke={ACCENT} strokeWidth="1.4" strokeLinejoin="round"
            opacity="0.95"
          />
          {/* base line on the floor (kerf cut accent)                  */}
          <line x1={w.a.x} y1={w.a.y} x2={w.b.x} y2={w.b.y}
                stroke={ACCENT} strokeWidth="1.6" strokeLinecap="round" />
        </g>
      ))}

      {/* Short walls (parallel to right wall)                          */}
      {shortWalls.map((w, j) => (
        <g key={`S${j}`}>
          <polygon
            points={`${fmt(w.a)} ${fmt(w.top_a)} ${fmt(w.top_b)} ${fmt(w.b)}`}
            fill={WOOD} stroke={ACCENT} strokeWidth="1.4" strokeLinejoin="round"
            opacity="0.95"
          />
          <line x1={w.a.x} y1={w.a.y} x2={w.b.x} y2={w.b.y}
                stroke={ACCENT} strokeWidth="1.6" strokeLinecap="round" />
        </g>
      ))}

      {/* Slot intersections: small notches where walls cross           */}
      <g stroke={ACCENT} strokeWidth="0.9" fill={ACCENT_S} opacity="0.85">
        {longWalls.flatMap((lw, i) =>
          shortWalls.map((sw, j) => {
            const u = (j + 1) / cols;
            const v = (i + 1) / rows;
            const c = top(P(u, v));
            return <circle key={`X${i}-${j}`} cx={c.x} cy={c.y} r="1.6" />;
          })
        )}
      </g>
    </Shell>
  );
}

/* ═══ POLYGON BOX — hexagonal prism, top opening visible ══════════════ */
function PolygonIllu() {
  // Real plywood palette tuned to match BasicIllu (light maple/birch).
  const FACE_FRONT = '#D9B27E';
  const FACE_SIDE  = '#E8C896';
  const FACE_TOP   = '#F2DDB0';
  const FLOOR      = '#FAEAC6';
  const SHADOW     = '#A87B3F';
  const OUTLINE    = '#3F2E1A';

  // Hexagonal open box centred at (130, 105). Same 3/4 view feel as
  // the basic box: floor visible through the opening, finger-joint
  // teeth where vertical panels meet.
  // Hex top vertices (order: right → upper-right → upper-left → left → lower-left → lower-right):
  const top = [
    [195, 70], [165, 50], [95, 50], [65, 70], [95, 90], [165, 90],
  ];
  // Bottom is offset down — only front three vertices are visible.
  const bot = top.map(([x, y]) => [x, y + 60]);

  // Helper to build polygon strings.
  const poly = (pts) => pts.map(([x, y]) => `${x},${y}`).join(' ');

  return (
    <Shell bg>
      {/* ── Back walls (peeking above the floor) ─────────────────────── */}
      <polygon points={poly([top[2], top[3], bot[3], bot[2]])}
               fill={FACE_SIDE} stroke={OUTLINE} strokeWidth="1.4" strokeLinejoin="round" />
      <polygon points={poly([top[1], top[2], bot[2], bot[1]])}
               fill={FACE_TOP} stroke={OUTLINE} strokeWidth="1.4" strokeLinejoin="round" />

      {/* ── Floor (cream hexagon seen through the opening) ─────────────── */}
      <polygon points={poly(bot)}
               fill={FLOOR} stroke={OUTLINE} strokeWidth="1.2" strokeLinejoin="round" opacity="0.95" />
      {/* subtle inner-rim shadow */}
      <polygon points={poly([bot[2], bot[1], [bot[1][0], bot[1][1] + 4], [bot[2][0], bot[2][1] + 4]])}
               fill={SHADOW} opacity="0.3" />

      {/* ── Right back face ─────────────────────────────────────────── */}
      <polygon points={poly([top[0], top[1], bot[1], bot[0]])}
               fill={FACE_SIDE} stroke={OUTLINE} strokeWidth="1.4" strokeLinejoin="round" />
      {/* ── Left back face ──────────────────────────────────────────── */}
      <polygon points={poly([top[3], top[4], bot[4], bot[3]])}
               fill={FACE_FRONT} stroke={OUTLINE} strokeWidth="1.4" strokeLinejoin="round" />
      {/* ── Front-right face ────────────────────────────────────────── */}
      <polygon points={poly([top[5], top[0], bot[0], bot[5]])}
               fill={FACE_FRONT} stroke={OUTLINE} strokeWidth="1.4" strokeLinejoin="round" />
      {/* ── Front-left face ─────────────────────────────────────────── */}
      <polygon points={poly([top[4], top[5], bot[5], bot[4]])}
               fill={FACE_FRONT} stroke={OUTLINE} strokeWidth="1.4" strokeLinejoin="round" />

      {/* ── Top rim (lit edges around the opening) ──────────────────── */}
      <polyline points={poly([top[0], top[1], top[2], top[3]])}
                fill="none" stroke={OUTLINE} strokeWidth="1.0" />

      {/* ── Finger-joint teeth on the visible vertical seams ────────── */}
      {/* Front-left vertical seam (between left and front-left faces) */}
      <g>
        {[0, 1, 2, 3, 4].map(i => (
          <rect key={`fl${i}`} x={top[4][0] - 3} y={top[4][1] + 8 + i * 9}
                width={6} height={5} fill={SHADOW} stroke={OUTLINE} strokeWidth="0.6" />
        ))}
      </g>
      {/* Front-right vertical seam (between front-right and right faces) */}
      <g>
        {[0, 1, 2, 3, 4].map(i => (
          <rect key={`fr${i}`} x={top[0][0] - 3} y={top[0][1] + 8 + i * 9}
                width={6} height={5} fill={SHADOW} stroke={OUTLINE} strokeWidth="0.6" />
        ))}
      </g>
      {/* Bottom-front teeth */}
      <g>
        {Array.from({ length: 7 }).map((_, i) => (
          <rect key={`b${i}`} x={bot[4][0] + 4 + i * 14} y={bot[4][1] - 4}
                width={9} height={5} fill={SHADOW} stroke={OUTLINE} strokeWidth="0.6" />
        ))}
      </g>

      {/* dimension hint */}
      <g fontFamily="JetBrains Mono, monospace" fontSize="9" fill={OUTLINE} opacity="0.6">
        <text x="130" y="178" textAnchor="middle">3–12 lados</text>
      </g>
    </Shell>
  );
}

/* ═══ CURVED BOX — kerf-bent plywood with realistic groove pattern ══ */
function CurvedIllu() {
  // Same plywood palette as Basic and Polygon — keeps cards coherent.
  const FACE_FRONT = '#D9B27E';
  const FACE_TOP   = '#F2DDB0';
  const FLOOR      = '#FAEAC6';
  const SHADOW     = '#A87B3F';
  const OUTLINE    = '#3F2E1A';
  // Kerf cuts read as DARKER grooves in the wood, not red lines.
  const KERF       = '#7A5630';

  return (
    <Shell bg>
      {/* ── Body (rounded rectangle, isometric-ish) ──────────────────── */}
      <path d="M 60,135 Q 60,90 100,80 L 160,80 Q 200,80 210,110 Q 210,150 175,158 L 105,158 Q 60,158 60,135 Z"
            fill={FACE_FRONT} stroke={OUTLINE} strokeWidth="1.4" strokeLinejoin="round" />

      {/* ── Top rim (the lit plywood edge around the opening) ────────── */}
      <path d="M 60,135 Q 60,90 100,80 L 160,80 Q 200,80 210,110 L 200,118 Q 200,90 165,90 L 105,90 Q 70,90 70,130 Z"
            fill={FACE_TOP} stroke={OUTLINE} strokeWidth="1.2" strokeLinejoin="round" />

      {/* ── Floor seen through the opening ───────────────────────────── */}
      <path d="M 70,130 Q 70,92 105,92 L 165,92 Q 198,92 200,118 Q 198,140 168,142 L 102,142 Q 72,140 70,130 Z"
            fill={FLOOR} stroke={OUTLINE} strokeWidth="0.9" opacity="0.9" />

      {/* ── Kerf cuts on the front face (the signature feature) ──────── */}
      {/* Drawn as fine darker grooves following the curved face — read
          as real plywood relief, not a graphic accent. */}
      <g stroke={KERF} strokeWidth="0.8" opacity="0.85" strokeLinecap="round">
        {Array.from({ length: 16 }).map((_, i) => {
          const x = 72 + i * 8.6;
          // top y follows curve; piecewise linearization
          const top = i < 3 ? 96 - i * 1.5 : (i < 13 ? 88 : 96 - (15 - i) * 1.5);
          const bot = i < 3 ? 154 - i * 0.5 : (i < 13 ? 158 : 154 - (15 - i) * 0.5);
          return <line key={i} x1={x} y1={top} x2={x} y2={bot} />;
        })}
      </g>

      {/* ── Bottom shadow gradient hint ─────────────────────────────── */}
      <path d="M 60,150 Q 60,158 100,158 L 175,158 Q 210,156 210,148"
            fill="none" stroke={SHADOW} strokeWidth="2" opacity="0.35" />

      <g fontFamily="JetBrains Mono, monospace" fontSize="9" fill={OUTLINE} opacity="0.6">
        <text x="130" y="178" textAnchor="middle">curva por kerf</text>
      </g>
    </Shell>
  );
}

/* ═══ CARDBOARD BOX — photorealistic studio shot ═════════════════════ */
function CardboardIllu() {
  return <PhotoIllu src="/preview/cardboard-box.webp" alt="Caixa de papelão RSC aberta" scale={1} />;
}

/* ═══ CARDBOARD BOX — legacy SVG (kept for reference, not exported) ══ */
function CardboardIlluSvg() {
  // Honest kraft-paper palette tuned for realism (not the cartoony
  // brown of before). Two faces lit, one face shadowed, plus a darker
  // inner cavity so the open top reads correctly.
  const KRAFT_FRONT = '#C28A55';   // front face (slightly shadowed)
  const KRAFT_SIDE  = '#D6A370';   // side face (lit)
  const KRAFT_RIM   = '#E8BE8C';   // top rim (most lit)
  const FLUTE_EDGE  = '#A06A38';   // exposed corrugated flute on rim
  const INNER       = '#7A4F25';   // inside cavity (darker)
  const SHADOW      = '#6E4825';   // soft drop shadow
  const OUTLINE     = '#2E1F10';

  return (
    <Shell bg>
      {/* ── Back wall ─────────────────────────────────────────────────── */}
      <polygon points="62,80 198,80 198,118 62,118"
               fill={KRAFT_SIDE} stroke={OUTLINE} strokeWidth="1.4" strokeLinejoin="round" />

      {/* ── Inner cavity (looking down into the open top) ─────────────── */}
      <polygon points="68,118 192,118 192,148 68,148"
               fill={INNER} stroke={OUTLINE} strokeWidth="1.0" strokeLinejoin="round" />
      {/* subtle inner-rim shadow */}
      <polygon points="68,118 192,118 188,124 72,124"
               fill="#000" opacity="0.25" />

      {/* ── Right side wall (3/4 perspective) ─────────────────────────── */}
      <polygon points="198,80 218,86 218,154 198,148"
               fill={KRAFT_FRONT} stroke={OUTLINE} strokeWidth="1.4" strokeLinejoin="round" />

      {/* ── Left side wall ─────────────────────────────────────────────── */}
      <polygon points="42,86 62,80 62,148 42,154"
               fill={KRAFT_SIDE} stroke={OUTLINE} strokeWidth="1.4" strokeLinejoin="round" />

      {/* ── Front panel ───────────────────────────────────────────────── */}
      <polygon points="62,118 198,118 198,148 62,148"
               fill={KRAFT_FRONT} stroke={OUTLINE} strokeWidth="1.4" strokeLinejoin="round" />

      {/* ── Top rim (visible kraft edge) ──────────────────────────────── */}
      <polygon points="62,80 198,80 200,76 60,76"
               fill={KRAFT_RIM} stroke={OUTLINE} strokeWidth="1.0" />
      <polygon points="42,86 62,80 60,76 40,82"
               fill={KRAFT_RIM} stroke={OUTLINE} strokeWidth="1.0" />
      <polygon points="198,80 218,86 220,82 200,76"
               fill={KRAFT_RIM} stroke={OUTLINE} strokeWidth="1.0" />

      {/* ── Corrugated flute (the wavy line on the rim — kraft signature) ── */}
      <g stroke={FLUTE_EDGE} strokeWidth="0.9" fill="none" opacity="0.95">
        <path d="M 62 78 q 4 -2 8 0 t 8 0 t 8 0 t 8 0 t 8 0 t 8 0 t 8 0 t 8 0 t 8 0 t 8 0 t 8 0 t 8 0 t 8 0 t 8 0 t 8 0 t 8 0 t 8 0" />
      </g>

      {/* ── Open top flaps (folded outward, on the back rim) ─────────── */}
      {/* back flap leaning back */}
      <polygon points="62,80 198,80 196,42 64,42"
               fill={KRAFT_SIDE} stroke={OUTLINE} strokeWidth="1.2" opacity="0.95" />
      {/* fold crease — subtle darker line, not a screaming red dash */}
      <line x1="62" y1="80" x2="198" y2="80"
            stroke={SHADOW} strokeWidth="0.8" opacity="0.6" strokeDasharray="3,2" />
      {/* corrugated edge on the flap top */}
      <g stroke={FLUTE_EDGE} strokeWidth="0.7" fill="none" opacity="0.85">
        <path d="M 64 42 q 4 -1.5 8 0 t 8 0 t 8 0 t 8 0 t 8 0 t 8 0 t 8 0 t 8 0 t 8 0 t 8 0 t 8 0 t 8 0 t 8 0 t 8 0 t 8 0 t 8 0" />
      </g>

      {/* ── Side flaps (small, leaning out to the sides) ──────────────── */}
      <polygon points="62,80 42,86 38,60 60,52"
               fill={KRAFT_FRONT} stroke={OUTLINE} strokeWidth="1.2" opacity="0.95" />
      <polygon points="198,80 218,86 222,60 200,52"
               fill={KRAFT_FRONT} stroke={OUTLINE} strokeWidth="1.2" opacity="0.95" />

      {/* ── Glue tab strip on the front-left edge ─────────────────────── */}
      <polygon points="58,118 62,118 62,148 58,146"
               fill={SHADOW} stroke={OUTLINE} strokeWidth="0.7" opacity="0.85" />

      <g fontFamily="JetBrains Mono, monospace" fontSize="9" fill={OUTLINE} opacity="0.6">
        <text x="130" y="172" textAnchor="middle">papelão ondulado</text>
      </g>
    </Shell>
  );
}

/* ═══ LUMINAIRE — photorealistic studio shot ═════════════════════════ */
function LuminaireIllu() {
  return <PhotoIllu src="/preview/luminaire.webp" alt="Luminária pendente de madeira com ripas curvas onduladas" scale={1.2} />;
}

/* ═══ LUMINAIRE-SHAPES — multi-shape luminaire studio shot ═══════════ */
function LuminaireShapesIllu() {
  return <PhotoIllu src="/preview/luminaire-shapes.webp" alt="Luminária pendente bojuda em ripas curvas de madeira" scale={1.2} />;
}

/* ════════════════════════════════════════════════════════════════════
 *  POST-FOCUS-JIG TOOL ILLUSTRATIONS
 * ════════════════════════════════════════════════════════════════════
 *  All artworks use the same 260×200 viewBox region (via <Shell/>),
 *  the shared plywood palette (WOOD / WOOD_D / WOOD_L), and the same
 *  EDGE color for line-art. Cyan ACCENT highlights the distinguishing
 *  feature ("what this tool actually does"). When a label is useful
 *  for legibility, the JetBrains Mono font matches the rest of the
 *  catalogue.
 */

/* ═══ GEAR BUILDER — meshed pair of involute spur gears ══════════════
 * Two interlocking gears rendered as a tooth-ring + center hub. The
 * larger gear is plywood-faced; the smaller "pinion" is cyan to call
 * out the meshing — the very feature the tool exists to design.
 */
function GearBuilderIllu() {
  // Procedurally generated tooth ring: addendum/dedendum approximated
  // as a star polygon. Visual fidelity > gear math accuracy here.
  const gearPath = (cx, cy, teeth, rOuter, rInner) => {
    const pts = [];
    const N = teeth * 2;
    for (let i = 0; i < N; i += 1) {
      const a = (i / N) * Math.PI * 2 - Math.PI / 2;
      const r = i % 2 === 0 ? rOuter : rInner;
      pts.push(`${cx + r * Math.cos(a)},${cy + r * Math.sin(a)}`);
    }
    return `M${pts.join(' L')} Z`;
  };
  return (
    <Shell bg>
      {/* Big plywood gear */}
      <g>
        <path d={gearPath(100, 105, 14, 70, 58)}
              fill={WOOD} stroke={EDGE} strokeWidth="1.6" strokeLinejoin="round" />
        {/* face groove (concentric) */}
        <circle cx="100" cy="105" r="50" fill="none" stroke={EDGE} strokeWidth="0.7" opacity="0.4" />
        {/* center hub + bore */}
        <circle cx="100" cy="105" r="14" fill={WOOD_L} stroke={EDGE} strokeWidth="1.2" />
        <circle cx="100" cy="105" r="6" fill={EDGE} />
        {/* keyway */}
        <rect x="98" y="93" width="4" height="6" fill={EDGE} />
      </g>
      {/* Small cyan pinion (mesh partner) */}
      <g>
        <path d={gearPath(196, 75, 10, 38, 30)}
              fill={ACCENT_S} stroke={EDGE} strokeWidth="1.4" strokeLinejoin="round" opacity="0.9" />
        <circle cx="196" cy="75" r="8" fill="#FFFFFF" stroke={EDGE} strokeWidth="1" />
        <circle cx="196" cy="75" r="3.5" fill={EDGE} />
      </g>
      {/* center-distance line + label */}
      <g stroke={ACCENT} strokeWidth="1" strokeDasharray="3,2" opacity="0.85">
        <line x1="100" y1="105" x2="196" y2="75" />
      </g>
      <g fontFamily="JetBrains Mono, monospace" fontSize="7" fill={EDGE}>
        <text x="148" y="98" textAnchor="middle" opacity="0.75">module · Z · α</text>
      </g>
    </Shell>
  );
}

/* ═══ BEND CALCULATOR — sheet metal with fold line + dim arrows ══════
 * Side view of a sheet bending: flat segment + angled segment with a
 * red fold line, K-factor neutral axis marked. Communicates "press
 * brake / sheet-metal-bend allowance" instantly.
 */
function BendCalculatorIllu() {
  // Steel-ish palette (cooler than plywood) — this is a metal tool.
  const STEEL    = '#CBD5E1';
  const STEEL_D  = '#94A3B8';
  return (
    <Shell bg>
      {/* The bent sheet (two segments forming an L at ~110°) */}
      <g stroke={EDGE} strokeWidth="1.6" strokeLinejoin="round">
        {/* horizontal segment (top sheet) */}
        <path d="M 35 95 L 130 95 L 130 105 L 35 105 Z" fill={STEEL} />
        {/* bend arc fillet */}
        <path d="M 130 95 Q 150 95 158 113 L 150 119 Q 145 105 130 105 Z"
              fill={STEEL_D} />
        {/* vertical-ish leaning segment */}
        <path d="M 158 113 L 215 145 L 210 154 L 150 119 Z" fill={STEEL} />
      </g>
      {/* Neutral axis (dashed cyan — the K-factor visualisation) */}
      <path d="M 35 100 L 130 100 Q 152 100 154 116 L 212 149"
            fill="none" stroke={ACCENT} strokeWidth="1.2" strokeDasharray="3,2.5" />
      {/* Fold-line indicator (red) */}
      <g stroke={RED} strokeWidth="2" strokeLinecap="round">
        <line x1="135" y1="78" x2="146" y2="92" />
        <line x1="135" y1="118" x2="146" y2="105" />
      </g>
      {/* Angle arc */}
      <path d="M 145 95 A 18 18 0 0 1 162 110" fill="none" stroke={EDGE} strokeWidth="1" opacity="0.7" />
      <text x="165" y="95" fontFamily="JetBrains Mono, monospace" fontSize="9" fill={EDGE} opacity="0.85">
        90°
      </text>
      {/* Dimension callout */}
      <g fontFamily="JetBrains Mono, monospace" fontSize="7" fill={EDGE} opacity="0.75">
        <text x="80" y="130" textAnchor="middle">flat → bent</text>
        <text x="80" y="142" textAnchor="middle">K-factor</text>
      </g>
      {/* R/T thickness markers */}
      <g stroke={EDGE} strokeWidth="0.8" opacity="0.6">
        <line x1="35" y1="88" x2="35" y2="112" />
        <line x1="30" y1="88" x2="40" y2="88" />
        <line x1="30" y1="112" x2="40" y2="112" />
      </g>
      <text x="22" y="103" fontFamily="JetBrains Mono, monospace" fontSize="7" fill={EDGE} opacity="0.75">T</text>
    </Shell>
  );
}

/* ═══ L-BRACKET — corner brace with mounting holes ═══════════════════
 * 3/4 perspective of a steel L-bracket, two legs at 90°, hole pattern
 * on each leg. This is the literal product the tool outputs.
 */
function LBracketIllu() {
  const STEEL    = '#CBD5E1';
  const STEEL_D  = '#94A3B8';
  const STEEL_L  = '#E2E8F0';
  return (
    <Shell bg>
      {/* Vertical leg (front face) */}
      <path d="M 70 35 L 130 35 L 130 130 L 70 130 Z"
            fill={STEEL} stroke={EDGE} strokeWidth="1.6" strokeLinejoin="round" />
      {/* Top edge (chamfered) */}
      <path d="M 70 35 L 78 28 L 138 28 L 130 35 Z"
            fill={STEEL_L} stroke={EDGE} strokeWidth="1.4" />
      {/* Right edge */}
      <path d="M 130 35 L 138 28 L 138 123 L 130 130 Z"
            fill={STEEL_D} stroke={EDGE} strokeWidth="1.4" />
      {/* Horizontal leg (foot) — viewed in foreshortened perspective */}
      <path d="M 70 130 L 130 130 L 200 165 L 140 165 Z"
            fill={STEEL_D} stroke={EDGE} strokeWidth="1.6" strokeLinejoin="round" />
      <path d="M 130 130 L 138 123 L 208 158 L 200 165 Z"
            fill={STEEL_L} stroke={EDGE} strokeWidth="1.4" />
      {/* Fold crease (red — the bend line) */}
      <line x1="70" y1="130" x2="138" y2="123" stroke={RED} strokeWidth="1.8" strokeDasharray="3,2" />
      {/* Mounting holes on vertical leg */}
      <g fill="#FFFFFF" stroke={EDGE} strokeWidth="1.1">
        <circle cx="90" cy="55" r="5" />
        <circle cx="110" cy="55" r="5" />
        <circle cx="90" cy="105" r="5" />
        <circle cx="110" cy="105" r="5" />
      </g>
      {/* Slot on horizontal leg */}
      <path d="M 155 148 a 4 4 0 0 1 4 -4 h 14 a 4 4 0 0 1 0 8 h -14 a 4 4 0 0 1 -4 -4 Z"
            fill="#FFFFFF" stroke={EDGE} strokeWidth="1.1" />
      {/* Cyan stiffener gusset highlight */}
      <path d="M 130 130 L 138 123 L 145 138 L 138 138 Z" fill={ACCENT_S} opacity="0.7" stroke={EDGE} strokeWidth="0.7" />
    </Shell>
  );
}

/* ═══ ENCLOSURE — electronics box with port cutouts ══════════════════
 * Iso view of a small instrument enclosure: U-shaped body + lid +
 * front panel with USB / jack / LED cutouts visible. Cyan rim around
 * one cutout calls out the "panel cutout editor" feature.
 */
function EnclosureIllu() {
  const STEEL    = '#CBD5E1';
  const STEEL_D  = '#94A3B8';
  const STEEL_L  = '#E2E8F0';
  return (
    <Shell bg>
      {/* Back/top face (lid) */}
      <path d="M 55 55 L 210 55 L 195 75 L 40 75 Z"
            fill={STEEL_L} stroke={EDGE} strokeWidth="1.5" strokeLinejoin="round" />
      {/* Front face with cutouts */}
      <rect x="40" y="75" width="155" height="80" fill={STEEL} stroke={EDGE} strokeWidth="1.6" />
      {/* Side face (right) */}
      <path d="M 195 75 L 210 55 L 210 135 L 195 155 Z"
            fill={STEEL_D} stroke={EDGE} strokeWidth="1.5" />
      {/* USB-A cutout */}
      <rect x="60" y="100" width="20" height="9" rx="1" fill="#1F2937" />
      <rect x="62" y="102" width="16" height="5" fill="#FFFFFF" />
      {/* 3.5mm jack (round) */}
      <circle cx="100" cy="104.5" r="6" fill="#1F2937" />
      <circle cx="100" cy="104.5" r="3" fill={EDGE} />
      <circle cx="100" cy="104.5" r="1.5" fill="#FFFFFF" />
      {/* LEDs (2) */}
      <circle cx="125" cy="100" r="3" fill="#10B981" />
      <circle cx="125" cy="110" r="3" fill="#FBBF24" />
      {/* Toggle switch hole */}
      <rect x="142" y="98" width="12" height="14" rx="2" fill="#1F2937" />
      {/* Cyan-highlighted "panel cutout editor" callout — a rectangular
          cutout being designed, with cyan dashed outline */}
      <rect x="60" y="125" width="100" height="20" rx="2"
            fill="none" stroke={ACCENT} strokeWidth="1.4" strokeDasharray="3,2" />
      <g fontFamily="JetBrains Mono, monospace" fontSize="6.5" fill={ACCENT} opacity="0.9">
        <text x="110" y="139" textAnchor="middle">DB9 · RJ45 · IEC</text>
      </g>
      {/* Bottom shadow */}
      <rect x="40" y="155" width="155" height="3" fill={EDGE} opacity="0.15" />
    </Shell>
  );
}

/* ═══ RACK PANEL — 19" 2U rack with mount holes + cutouts ════════════
 * Front view of an EIA-310 rack panel with the iconic squared mounting
 * holes at each end, a display cutout, knob hole and ventilation slots.
 */
function RackPanelIllu() {
  const STEEL    = '#3F3F46';   // black anodised aluminium
  const STEEL_L  = '#52525B';
  const STEEL_D  = '#27272A';
  return (
    <Shell bg>
      {/* The 2U panel itself (wide, short) */}
      <rect x="25" y="60" width="210" height="90" rx="2"
            fill={STEEL} stroke={EDGE} strokeWidth="1.4" />
      {/* Subtle bevel highlight */}
      <rect x="27" y="62" width="206" height="6" fill={STEEL_L} opacity="0.6" />
      <rect x="27" y="142" width="206" height="6" fill={STEEL_D} opacity="0.5" />
      {/* EIA mounting holes (4) — squared cage-nut style */}
      <g fill="#0A0A0A" stroke="#000" strokeWidth="0.6">
        <rect x="34" y="75" width="9" height="9" rx="1" />
        <rect x="34" y="126" width="9" height="9" rx="1" />
        <rect x="217" y="75" width="9" height="9" rx="1" />
        <rect x="217" y="126" width="9" height="9" rx="1" />
      </g>
      {/* LCD/OLED display cutout (cyan rim = the design feature) */}
      <rect x="60" y="83" width="60" height="44" rx="2" fill="#0A0A0A"
            stroke={ACCENT} strokeWidth="1.4" />
      <text x="90" y="109" textAnchor="middle"
            fontFamily="JetBrains Mono, monospace" fontSize="9"
            fill={ACCENT_S}>2U · 19"</text>
      {/* Rotary encoder hole + knob */}
      <circle cx="145" cy="105" r="11" fill={STEEL_D} stroke={EDGE} strokeWidth="0.8" />
      <circle cx="145" cy="105" r="7" fill={STEEL_L} stroke={EDGE} strokeWidth="0.8" />
      <line x1="145" y1="98" x2="145" y2="104" stroke="#FFFFFF" strokeWidth="1.4" strokeLinecap="round" />
      {/* Vent slots (5 vertical) */}
      <g fill="#0A0A0A">
        {[170, 178, 186, 194, 202].map((x) => (
          <rect key={x} x={x} y="90" width="3" height="30" rx="1" />
        ))}
      </g>
      {/* BNC connector (small round port) */}
      <circle cx="180" cy="132" r="4" fill={STEEL_D} stroke={EDGE} strokeWidth="0.7" />
      <circle cx="180" cy="132" r="1.5" fill="#FBBF24" />
    </Shell>
  );
}

/* ═══ SLICE & STACK — exploded 3D sculpture as layered slices ════════
 * Stacked horizontal slices showing how a 3D head/bust is broken into
 * flat pieces for laser/CNC. Each slice is offset slightly to suggest
 * the assembly direction.
 */
/* ═══ 3D POLYGON — low-poly metal head sculpture (Doberman silhouette) ═
 * Stylised side-view of a triangulated dog/wolf head, like the metal
 * sheet-metal sculptures the tool produces. Black triangles on the
 * wood background, with a hint of the unfolded sheet on the right.
 *
 * Coordinates are eyeballed to evoke a Doberman's pointy ears + long
 * snout silhouette. Each <polygon> is a triangle with a slightly
 * different black tone to suggest folded panels catching light.
 */
function Polygon3DIllu() {
  // Dark-tone palette for the metallic head (different shades suggest
  // dihedral folds catching the light).
  const T1 = '#0a0a0a'; // darkest
  const T2 = '#1f1f1f';
  const T3 = '#2d2d2d';
  const T4 = '#404040';
  const T5 = '#525252';
  // Doberman head — series of triangles, side view facing right.
  const head = [
    // ear back
    { p: '70,32 80,18 88,42',           f: T1 },
    { p: '80,18 95,30 88,42',           f: T2 },
    { p: '95,30 108,22 105,48',         f: T3 },
    { p: '108,22 122,28 105,48',        f: T2 },
    // forehead / top of skull
    { p: '88,42 105,48 110,68',         f: T2 },
    { p: '105,48 122,28 135,55',        f: T4 },
    { p: '105,48 135,55 110,68',        f: T3 },
    { p: '135,55 110,68 145,80',        f: T2 },
    // muzzle / snout
    { p: '110,68 145,80 130,95',        f: T1 },
    { p: '145,80 170,82 155,98',        f: T3 },
    { p: '145,80 155,98 130,95',        f: T2 },
    { p: '170,82 185,90 175,110',       f: T4 },
    { p: '170,82 175,110 155,98',       f: T2 },
    { p: '185,90 195,108 175,110',      f: T3 },
    // jaw / lower
    { p: '130,95 155,98 142,118',       f: T1 },
    { p: '155,98 175,110 160,125',      f: T2 },
    { p: '175,110 195,108 180,128',     f: T3 },
    { p: '155,98 160,125 142,118',      f: T2 },
    { p: '175,110 180,128 160,125',     f: T1 },
    // neck / chest
    { p: '88,42 110,68 95,90',          f: T3 },
    { p: '110,68 130,95 95,90',         f: T2 },
    { p: '95,90 130,95 105,130',        f: T1 },
    { p: '130,95 142,118 115,140',      f: T2 },
    { p: '142,118 160,125 140,148',     f: T3 },
    { p: '160,125 180,128 165,150',     f: T4 },
    { p: '105,130 115,140 100,158',     f: T2 },
    { p: '115,140 140,148 125,162',     f: T1 },
    { p: '140,148 165,150 152,165',     f: T2 },
    // back of neck (filling)
    { p: '95,90 105,130 80,118',        f: T3 },
    { p: '105,130 100,158 78,140',      f: T4 },
  ];

  return (
    <Shell bg>
      {/* Soft ground shadow under the head */}
      <ellipse cx="130" cy="172" rx="78" ry="5" fill={EDGE} opacity="0.18" />

      {/* The low-poly head itself */}
      <g stroke="#000" strokeWidth="0.6" strokeLinejoin="round">
        {head.map((tri, i) => (
          <polygon key={i} points={tri.p} fill={tri.f} />
        ))}
      </g>

      {/* "Eye" — single bright spot to give the head life */}
      <circle cx="120" cy="65" r="1.8" fill={ACCENT_S} opacity="0.9" />

      {/* On the right: a small "unfolded sheet" hint — 4 flat triangles
          arranged on a grid, evoking the laser cut output. */}
      <g transform="translate(205, 38)">
        {/* sheet rectangle */}
        <rect x="0" y="0" width="38" height="48" rx="2"
              fill={WOOD_L} stroke={EDGE} strokeWidth="1" />
        {/* faint grid */}
        <g stroke={EDGE} strokeWidth="0.25" opacity="0.25">
          <line x1="0" y1="16" x2="38" y2="16" />
          <line x1="0" y1="32" x2="38" y2="32" />
          <line x1="13" y1="0" x2="13" y2="48" />
          <line x1="25" y1="0" x2="25" y2="48" />
        </g>
        {/* triangles laid flat — preserving the pieces' contour */}
        <g fill="none" stroke={EDGE} strokeWidth="0.9" strokeLinejoin="round">
          <polygon points="3,4 12,4 8,14" />
          <polygon points="15,4 24,4 20,14" />
          <polygon points="27,4 36,4 32,14" />
          <polygon points="3,18 12,18 8,28" />
          <polygon points="15,18 24,18 20,28" />
          <polygon points="27,18 36,18 32,28" />
          <polygon points="3,34 12,34 8,44" />
          <polygon points="15,34 24,34 20,44" />
        </g>
      </g>

      {/* Arrow + label connecting head → sheet, evoking "unfold" */}
      <g stroke={ACCENT} strokeWidth="1.4" fill="none" strokeLinecap="round">
        <path d="M 198 92 Q 210 86 215 80" />
        <path d="M 213 76 L 217 80 L 213 83" />
      </g>
      <text x="225" y="100" textAnchor="middle"
            fontFamily="JetBrains Mono, monospace" fontSize="6"
            fill={ACCENT}>unfold</text>
    </Shell>
  );
}

function SliceStackIllu() {
  // The slice silhouettes approximate a bust profile: widest near the
  // middle (shoulders), narrowing toward top (head) and bottom (base).
  const slices = [
    { y: 50,  w: 24, x: 118 },
    { y: 62,  w: 34, x: 113 },
    { y: 74,  w: 46, x: 107 },
    { y: 86,  w: 58, x: 101 },
    { y: 98,  w: 72, x:  94 },
    { y: 110, w: 86, x:  87 },
    { y: 122, w: 96, x:  82 },
    { y: 134, w: 100, x: 80 },
    { y: 146, w: 96, x:  82 },
  ];
  return (
    <Shell bg>
      {/* Drop shadow trail under the stack */}
      <ellipse cx="130" cy="170" rx="80" ry="6" fill={EDGE} opacity="0.15" />
      {/* Slices (back-to-front so foreshortening looks right) */}
      {slices.map((s, i) => {
        const isHighlight = i === 5; // pick one slice to highlight in cyan
        return (
          <g key={i}>
            {/* slice face (top) */}
            <ellipse cx={s.x + s.w / 2} cy={s.y} rx={s.w / 2} ry={3}
                     fill={isHighlight ? ACCENT_S : WOOD_L}
                     stroke={EDGE} strokeWidth="1" />
            {/* slice side (thickness) */}
            <path d={`M ${s.x} ${s.y} L ${s.x} ${s.y + 8}
                      Q ${s.x + s.w / 2} ${s.y + 11} ${s.x + s.w} ${s.y + 8}
                      L ${s.x + s.w} ${s.y} Q ${s.x + s.w / 2} ${s.y + 3} ${s.x} ${s.y} Z`}
                  fill={isHighlight ? ACCENT : WOOD_D} stroke={EDGE} strokeWidth="1" />
          </g>
        );
      })}
      {/* numbering on a few slices */}
      <g fontFamily="JetBrains Mono, monospace" fontSize="6" fill={EDGE} opacity="0.7">
        <text x={slices[1].x + slices[1].w / 2} y={slices[1].y + 1} textAnchor="middle">02</text>
        <text x={slices[4].x + slices[4].w / 2} y={slices[4].y + 1} textAnchor="middle">05</text>
        <text x={slices[7].x + slices[7].w / 2} y={slices[7].y + 1} textAnchor="middle">08</text>
      </g>
      {/* assembly arrow on the right */}
      <g stroke={ACCENT} strokeWidth="1.4" fill="none" strokeLinecap="round">
        <line x1="220" y1="60" x2="220" y2="150" />
        <path d="M 215 145 L 220 152 L 225 145" />
      </g>
      <text x="220" y="170" textAnchor="middle"
            fontFamily="JetBrains Mono, monospace" fontSize="6.5"
            fill={ACCENT}>stack</text>
    </Shell>
  );
}

/* ═══ FILL PATTERN — perforated panel sampler (4 patterns) ═══════════
 * A rectangular panel divided into 4 quadrants, each showing a
 * different pattern: hex honeycomb, Voronoi cells, concentric circles,
 * and triangle mesh. Captures the "8 patterns to choose from" pitch.
 */
function FillPatternIllu() {
  return (
    <Shell bg>
      {/* Panel base */}
      <rect x="35" y="40" width="190" height="135" rx="3"
            fill={WOOD} stroke={EDGE} strokeWidth="1.6" />
      {/* Quadrant divider lines (faint) */}
      <line x1="130" y1="40" x2="130" y2="175" stroke={EDGE} strokeWidth="0.5" opacity="0.3" strokeDasharray="2,2" />
      <line x1="35" y1="107" x2="225" y2="107" stroke={EDGE} strokeWidth="0.5" opacity="0.3" strokeDasharray="2,2" />

      {/* Q1 (top-left): hexagonal honeycomb */}
      <g fill={EDGE}>
        {[0, 1, 2].map(r => (
          [0, 1, 2, 3].map(c => {
            const cx = 50 + c * 18 + (r % 2 ? 9 : 0);
            const cy = 52 + r * 16;
            if (cx > 122) return null;
            const a = 6.5;
            const pts = Array.from({ length: 6 }).map((_, i) => {
              const ang = (i / 6) * Math.PI * 2 + Math.PI / 6;
              return `${cx + a * Math.cos(ang)},${cy + a * Math.sin(ang)}`;
            }).join(' ');
            return <polygon key={`h${r}-${c}`} points={pts} />;
          })
        ))}
      </g>

      {/* Q2 (top-right): concentric rings */}
      <g fill="none" stroke={EDGE} strokeWidth="1.2">
        <circle cx="178" cy="72" r="6" />
        <circle cx="178" cy="72" r="12" />
        <circle cx="178" cy="72" r="18" />
        <circle cx="178" cy="72" r="24" />
      </g>

      {/* Q3 (bottom-left): triangle mesh */}
      <g stroke={EDGE} strokeWidth="0.9" fill="none">
        {[0, 1, 2, 3, 4].map(i => (
          <line key={`d${i}`} x1={42 + i * 18} y1="170" x2={60 + i * 18} y2="115" />
        ))}
        {[0, 1, 2, 3, 4].map(i => (
          <line key={`d2${i}`} x1={42 + i * 18} y1="115" x2={60 + i * 18} y2="170" />
        ))}
        <line x1="42" y1="115" x2="122" y2="115" />
        <line x1="42" y1="170" x2="122" y2="170" />
      </g>

      {/* Q4 (bottom-right): Voronoi-ish cells (handcrafted) */}
      <g stroke={EDGE} strokeWidth="1" fill="none">
        <path d="M 138 115 L 155 122 L 168 115" />
        <path d="M 155 122 L 162 140 L 180 145" />
        <path d="M 180 145 L 195 132 L 215 138" />
        <path d="M 195 132 L 205 115" />
        <path d="M 162 140 L 150 160 L 168 170" />
        <path d="M 168 170 L 185 160 L 195 170" />
        <path d="M 185 160 L 180 145" />
        <path d="M 215 138 L 218 158 L 200 170" />
        <path d="M 200 170 L 195 170" />
      </g>

      {/* Highlight one quadrant in cyan as "selected pattern" */}
      <rect x="36" y="41" width="92" height="64" rx="2" fill="none"
            stroke={ACCENT} strokeWidth="1.5" strokeDasharray="3,2" />
    </Shell>
  );
}

/* ═══ CONNECTED TEXT (Stencil) — letters with bridges ════════════════
 * The word "FAB" rendered as stencil cut-out: the inner counter of A
 * is held in place by visible cyan "bridges" — the tool's headline
 * feature.
 */
function ConnectedTextIllu() {
  return (
    <Shell bg>
      {/* plywood plate the stencil is cut from */}
      <rect x="30" y="55" width="200" height="100" rx="3"
            fill={WOOD} stroke={EDGE} strokeWidth="1.6" />
      <rect x="33" y="58" width="194" height="94" rx="2"
            fill="none" stroke={EDGE} strokeWidth="0.6" opacity="0.4" strokeDasharray="2,2" />

      {/* Letter F (cutout = dark) */}
      <path d="M 50 75 L 80 75 L 80 82 L 58 82 L 58 100 L 74 100 L 74 107
               L 58 107 L 58 135 L 50 135 Z"
            fill={EDGE} />

      {/* Letter A (cutout = dark) — counter is the inner triangle */}
      <path d="M 100 135 L 115 75 L 125 75 L 140 135 L 132 135
               L 129 122 L 111 122 L 108 135 Z
               M 113 115 L 127 115 L 122 92 L 118 92 Z"
            fill={EDGE} fillRule="evenodd" />
      {/* Stencil bridges holding the A's inner counter (cyan = the
          tool's signature feature). Two bridges left+right. */}
      <g fill={ACCENT}>
        <rect x="108" y="103" width="6" height="4" />
        <rect x="126" y="103" width="6" height="4" />
      </g>

      {/* Letter B (cutout = dark) */}
      <path d="M 155 75 L 178 75 Q 192 75 192 88 Q 192 96 184 100
               Q 195 103 195 115 Q 195 135 178 135 L 155 135 Z
               M 163 82 L 163 98 L 176 98 Q 184 98 184 90 Q 184 82 176 82 Z
               M 163 105 L 163 128 L 177 128 Q 187 128 187 116 Q 187 105 177 105 Z"
            fill={EDGE} fillRule="evenodd" />
      {/* Bridges on B's two counters */}
      <g fill={ACCENT}>
        <rect x="174" y="88" width="4" height="3" />
        <rect x="174" y="115" width="4" height="3" />
      </g>

      {/* Callout label */}
      <text x="130" y="170" textAnchor="middle"
            fontFamily="JetBrains Mono, monospace" fontSize="7"
            fill={ACCENT}>stencil bridges</text>
    </Shell>
  );
}

/* ═══ SINGLE-LINE TEXT — Hershey stroke handwriting ══════════════════
 * The word "abc" written as continuous single-stroke polylines, with
 * one stroke endpoint marked by a tiny plotter-pen icon to evoke "the
 * laser/pen follows ONE line — no fill, no outline".
 */
function SingleLineTextIllu() {
  return (
    <Shell bg>
      {/* Sheet of paper / plate background */}
      <rect x="25" y="50" width="210" height="110" rx="3"
            fill="#FFFFFF" stroke={EDGE} strokeWidth="1.4" />
      {/* baseline (guide) */}
      <line x1="35" y1="130" x2="225" y2="130"
            stroke={EDGE} strokeWidth="0.5" opacity="0.3" strokeDasharray="3,2" />
      <line x1="35" y1="85" x2="225" y2="85"
            stroke={EDGE} strokeWidth="0.5" opacity="0.3" strokeDasharray="3,2" />

      {/* Single-line "a" — one continuous bezier-ish polyline */}
      <path d="M 80 105 Q 60 95 60 115 Q 60 135 80 130 Q 92 125 92 110
               L 92 130"
            fill="none" stroke={EDGE} strokeWidth="2.2"
            strokeLinecap="round" strokeLinejoin="round" />

      {/* Single-line "b" — one stroke */}
      <path d="M 110 75 L 110 130 Q 110 132 120 130
               Q 135 125 135 115 Q 135 100 120 100 Q 110 102 110 110"
            fill="none" stroke={EDGE} strokeWidth="2.2"
            strokeLinecap="round" strokeLinejoin="round" />

      {/* Single-line "c" — one stroke */}
      <path d="M 175 108 Q 155 95 152 115 Q 152 135 175 128"
            fill="none" stroke={EDGE} strokeWidth="2.2"
            strokeLinecap="round" strokeLinejoin="round" />

      {/* Cyan "plotter pen" at one stroke endpoint to make the
          single-line concept explicit */}
      <g transform="translate(175,128)">
        <line x1="0" y1="0" x2="14" y2="-22" stroke={ACCENT} strokeWidth="2.2" strokeLinecap="round" />
        <polygon points="0,0 4,-5 -2,-3" fill={ACCENT} />
        <rect x="13" y="-30" width="6" height="12" fill={ACCENT_S} stroke={EDGE} strokeWidth="0.7" />
      </g>

      <text x="130" y="170" textAnchor="middle"
            fontFamily="JetBrains Mono, monospace" fontSize="7"
            fill={ACCENT}>1 stroke · no fill</text>
    </Shell>
  );
}

/* ═══ STIR STICK — three drink stirrers with decorative tops ═════════
 * A trio of laser-cut stirrers (heart, star, cocktail glass) lined up
 * like in a glass. Cyan engraved letter "M" on one shows the optional
 * personalisation feature.
 */
function StirStickIllu() {
  // Helper: a stirrer is a tall thin rectangle (stick) + a decorative top.
  return (
    <Shell bg>
      {/* Glass rim (suggestion) */}
      <path d="M 70 145 Q 130 158 190 145"
            fill="none" stroke={EDGE} strokeWidth="1.2" opacity="0.45" />
      <path d="M 75 152 Q 130 168 185 152"
            fill="none" stroke={EDGE} strokeWidth="0.8" opacity="0.3" />

      {/* Stick 1 — heart top */}
      <g>
        <rect x="92" y="80" width="6" height="80" fill={WOOD_L} stroke={EDGE} strokeWidth="0.8" />
        <path d="M 95 70 Q 85 60 85 50 Q 85 40 95 45 Q 105 40 105 50 Q 105 60 95 70 Z"
              fill={RED} stroke={EDGE} strokeWidth="1.2" />
      </g>

      {/* Stick 2 (center) — star top, with engraved "M" (cyan) */}
      <g>
        <rect x="127" y="65" width="6" height="95" fill={WOOD} stroke={EDGE} strokeWidth="0.9" />
        {/* engraved "M" on the stick */}
        <text x="130" y="110" textAnchor="middle"
              fontFamily="JetBrains Mono, monospace" fontSize="6"
              fill={ACCENT} transform="rotate(90 130 110)">M ★ A</text>
        {/* star */}
        <path d="M 130 32 L 134 46 L 149 46 L 137 54 L 141 68
                 L 130 60 L 119 68 L 123 54 L 111 46 L 126 46 Z"
              fill="#FBBF24" stroke={EDGE} strokeWidth="1.2" strokeLinejoin="round" />
      </g>

      {/* Stick 3 — cocktail glass top */}
      <g>
        <rect x="162" y="75" width="6" height="85" fill={WOOD_L} stroke={EDGE} strokeWidth="0.8" />
        {/* martini glass */}
        <path d="M 150 38 L 180 38 L 165 60 Z"
              fill={ACCENT_S} stroke={EDGE} strokeWidth="1.2" strokeLinejoin="round" />
        <line x1="165" y1="60" x2="165" y2="75" stroke={EDGE} strokeWidth="1.2" />
        <ellipse cx="165" cy="76" rx="6" ry="1.4" fill={EDGE} />
        {/* olive */}
        <circle cx="170" cy="44" r="2.5" fill="#10B981" stroke={EDGE} strokeWidth="0.5" />
      </g>
    </Shell>
  );
}

/* ═══ NEON SIGN — glowing "LED" on a dark backing plate ══════════════
 * Mimics the actual preview the user sees in the tool: dark rounded
 * plate, pink neon tape spelling "LED", soft glow halo. This is the
 * most literal "what you get" illustration of the set.
 */
function NeonSignIllu() {
  return (
    <Shell bg>
      <defs>
        <filter id="neonIcoGlow" x="-50%" y="-50%" width="200%" height="200%">
          <feGaussianBlur in="SourceGraphic" stdDeviation="2" result="blur" />
          <feMerge>
            <feMergeNode in="blur" />
            <feMergeNode in="blur" />
            <feMergeNode in="SourceGraphic" />
          </feMerge>
        </filter>
      </defs>

      {/* Dark backing plate (rounded rectangle) */}
      <rect x="25" y="55" width="210" height="100" rx="14"
            fill="#1F2937" stroke={EDGE} strokeWidth="1.6" />
      {/* subtle inner highlight */}
      <rect x="29" y="59" width="202" height="92" rx="11"
            fill="none" stroke="#52525B" strokeWidth="0.6" opacity="0.6" />

      {/* Outer pink glow (large blur) */}
      <g filter="url(#neonIcoGlow)" opacity="0.45">
        <path d="M 60 130 L 60 80 M 60 130 L 90 130"
              stroke="#EC4899" strokeWidth="9" strokeLinecap="round" fill="none" />
        <path d="M 110 130 L 110 80 L 145 80 M 110 105 L 138 105 M 110 130 L 145 130"
              stroke="#EC4899" strokeWidth="9" strokeLinecap="round" fill="none" />
        <path d="M 165 130 L 165 80 L 205 80 L 205 130 L 165 130"
              stroke="#EC4899" strokeWidth="9" strokeLinecap="round" strokeLinejoin="round" fill="none" />
      </g>

      {/* Pink neon tape (mid layer) */}
      <g>
        <path d="M 60 130 L 60 80 M 60 130 L 90 130"
              stroke="#F472B6" strokeWidth="6" strokeLinecap="round" fill="none" opacity="0.95" />
        <path d="M 110 130 L 110 80 L 145 80 M 110 105 L 138 105 M 110 130 L 145 130"
              stroke="#F472B6" strokeWidth="6" strokeLinecap="round" fill="none" opacity="0.95" />
        <path d="M 165 130 L 165 80 L 205 80 L 205 130 L 165 130"
              stroke="#F472B6" strokeWidth="6" strokeLinecap="round" strokeLinejoin="round" fill="none" opacity="0.95" />
      </g>

      {/* White core (the LED interior) */}
      <g>
        <path d="M 60 130 L 60 80 M 60 130 L 90 130"
              stroke="#FFFFFF" strokeWidth="2.2" strokeLinecap="round" fill="none" />
        <path d="M 110 130 L 110 80 L 145 80 M 110 105 L 138 105 M 110 130 L 145 130"
              stroke="#FFFFFF" strokeWidth="2.2" strokeLinecap="round" fill="none" />
        <path d="M 165 130 L 165 80 L 205 80 L 205 130 L 165 130"
              stroke="#FFFFFF" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" fill="none" />
      </g>

      {/* Power cord exiting bottom */}
      <path d="M 130 155 Q 135 165 130 175 Q 125 180 135 185"
            fill="none" stroke="#0A0A0A" strokeWidth="1.8" strokeLinecap="round" />
      <circle cx="135" cy="186" r="2" fill="#FBBF24" />
    </Shell>
  );
}

/* ─── LED Pixel Board ───────────────────────────────────────────────────
   Drill-template generator for classic backlit LED signs ("ABERTO",
   "CAIXA", "Café"). The card shows a dark plate with the word "LED"
   formed by colored dot-LEDs (red / yellow / green) and a single row
   of white border LEDs running around the perimeter — the exact output
   the tool produces. Each dot is rendered three times (large blur,
   mid-saturation fill, white core) so it reads as a glowing physical
   LED rather than a flat circle.                                       */
function LedPixelBoardIllu() {
  // Helper: a single glowing LED (3 stacked circles for the bloom effect)
  // — kept local so the shape stays consistent across content and border.
  const Led = ({ x, y, color, r = 3.2 }) => (
    <>
      <circle cx={x} cy={y} r={r + 2.2} fill={color} opacity="0.25" filter="url(#ledIcoGlow)" />
      <circle cx={x} cy={y} r={r}        fill={color} opacity="0.95" />
      <circle cx={x} cy={y} r={r * 0.45} fill="#FFFFFF" opacity="0.9" />
    </>
  );

  // Centerline-sampled coordinates for the letters "L E D" — each letter
  // occupies a ~38×60 box on the plate. The shapes follow the stroke
  // skeleton (single-line) so the result mimics what the live tool
  // produces from the singleLineTextGenerator.
  const dotsL = [ // red
    [60, 70], [60, 80], [60, 90], [60, 100], [60, 110], [60, 120], [60, 130],
    [68, 130], [76, 130], [84, 130],
  ];
  const dotsE = [ // yellow
    [105, 70], [105, 80], [105, 90], [105, 100], [105, 110], [105, 120], [105, 130],
    [113, 70], [121, 70], [129, 70],
    [113, 100], [121, 100],
    [113, 130], [121, 130], [129, 130],
  ];
  const dotsD = [ // green
    [155, 70], [155, 80], [155, 90], [155, 100], [155, 110], [155, 120], [155, 130],
    [163, 70], [171, 70], [179, 72],
    [185, 80], [187, 90], [187, 100], [187, 110], [185, 120],
    [179, 128], [171, 130], [163, 130],
  ];

  // Single row of white border LEDs around the inner perimeter of the plate
  const borderDots = [];
  // Top row
  for (let x = 35; x <= 225; x += 10) borderDots.push([x, 50]);
  // Bottom row
  for (let x = 35; x <= 225; x += 10) borderDots.push([x, 150]);
  // Left column
  for (let y = 60; y <= 140; y += 10) borderDots.push([35, y]);
  // Right column
  for (let y = 60; y <= 140; y += 10) borderDots.push([225, y]);

  return (
    <Shell bg>
      <defs>
        <filter id="ledIcoGlow" x="-200%" y="-200%" width="500%" height="500%">
          <feGaussianBlur in="SourceGraphic" stdDeviation="2.4" />
        </filter>
        {/* Subtle radial gradient so the plate centre looks brighter
            (mimics the backlight bleed of a real LED panel) */}
        <radialGradient id="ledPlateGrad" cx="50%" cy="50%" r="60%">
          <stop offset="0%"  stopColor="#1E293B" />
          <stop offset="100%" stopColor="#0A0A0A" />
        </radialGradient>
      </defs>

      {/* Dark backing plate (the drilled MDF / acrylic panel) */}
      <rect x="20" y="40" width="220" height="120" rx="10"
            fill="url(#ledPlateGrad)" stroke={EDGE} strokeWidth="1.6" />
      {/* Inner highlight outline */}
      <rect x="24" y="44" width="212" height="112" rx="7"
            fill="none" stroke="#3F3F46" strokeWidth="0.5" opacity="0.7" />

      {/* ── Border LEDs (single row of small white dots) ───────────── */}
      <g>
        {borderDots.map(([x, y], i) => (
          <Led key={`b${i}`} x={x} y={y} color="#F8FAFC" r={1.6} />
        ))}
      </g>

      {/* ── Content LEDs — "L" in red, "E" in yellow, "D" in green ── */}
      <g>
        {dotsL.map(([x, y], i) => (
          <Led key={`l${i}`} x={x} y={y} color="#EF4444" r={3.2} />
        ))}
      </g>
      <g>
        {dotsE.map(([x, y], i) => (
          <Led key={`e${i}`} x={x} y={y} color="#FBBF24" r={3.2} />
        ))}
      </g>
      <g>
        {dotsD.map(([x, y], i) => (
          <Led key={`d${i}`} x={x} y={y} color="#22C55E" r={3.2} />
        ))}
      </g>

      {/* Power cord exiting bottom-right (echoes the NeonSign card) */}
      <path d="M 210 160 Q 215 170 208 178 Q 200 184 212 188"
            fill="none" stroke="#0A0A0A" strokeWidth="1.6" strokeLinecap="round" />
      <circle cx="212" cy="189" r="2" fill="#FBBF24" />
    </Shell>
  );
}

/* ═══ BOARD CASE — Real photo of acrylic enclosure for dev boards ═══
 * Replaced the old isometric SVG illustration with a real photograph
 * of a neon-green acrylic finger-joint case housing a dev board
 * (Raspberry Pi 5 with USB-C, USB-A, RJ45 ports). Communicates the
 * tool's actual output far better than the hand-drawn SVG could.
 */
function BoardCaseIllu() {
  return <PhotoIllu src="/preview/board-case.webp" alt="Case de acrílico verde fluorescente em finger joint encerrando placa de desenvolvimento" scale={1.2} />;
}

/* ─── AI DXF Generator ─────────────────────────────────────────────────
   Visual hook: a sparkle "AI" prompt bubble on the left + an arrow + a
   black-and-white intricate cut-out plate on the right (the kind of
   premium pierced metal panel the tool produces). The plate uses an
   "all-connected" mandala-ish silhouette to communicate the "100%
   conectado" rule that defines the tool. */
function AIDxfGenIllu() {
  return (
    <Shell bg>
      {/* LEFT — "prompt bubble" with a sparkle, evoking the chat-style input */}
      <g>
        {/* Bubble body */}
        <rect x="22" y="58" width="98" height="84" rx="14"
              fill="#FFFFFF" stroke={EDGE} strokeWidth="1.4" />
        {/* Bubble tail pointing right */}
        <path d="M 116 116 L 132 124 L 116 130 Z"
              fill="#FFFFFF" stroke={EDGE} strokeWidth="1.4" />
        {/* Text-line placeholders */}
        <rect x="34" y="70"  width="64" height="5" rx="2.5" fill="#E2E8F0" />
        <rect x="34" y="82"  width="74" height="5" rx="2.5" fill="#E2E8F0" />
        <rect x="34" y="94"  width="56" height="5" rx="2.5" fill="#E2E8F0" />
        <rect x="34" y="106" width="44" height="5" rx="2.5" fill="#E2E8F0" />
        {/* Sparkle icon top-left */}
        <g transform="translate(36 124)">
          <path d="M 0 -7 L 1.5 -1.5 L 7 0 L 1.5 1.5 L 0 7 L -1.5 1.5 L -7 0 L -1.5 -1.5 Z"
                fill="#06B6D4" />
        </g>
        <text x="50" y="129" fontFamily="Inter, sans-serif" fontSize="9" fontWeight="700" fill="#06B6D4">AI</text>
      </g>

      {/* CENTER — flowing arrow */}
      <g stroke={EDGE} strokeWidth="1.5" fill="none" strokeLinecap="round">
        <path d="M 138 100 L 162 100" />
        <path d="M 158 95 L 164 100 L 158 105" />
      </g>

      {/* RIGHT — premium pierced black plate (B&W, all connected) */}
      <g>
        {/* Plate base — solid black rectangle (rounded) */}
        <rect x="168" y="48" width="80" height="104" rx="8"
              fill="#0F172A" stroke={EDGE} strokeWidth="1.4" />

        {/* CUT-OUT motif: a series of organic interconnected loops + leaves.
            Every white shape "kisses" its neighbours so the design stays
            100% connected — illustrating the tool's signature rule. */}

        {/* Center circular vent (perfectly connected via 4 spokes) */}
        <circle cx="208" cy="84" r="11" fill="#FFFFFF" />
        {/* Spokes break the circle into a "wheel" silhouette */}
        <g fill="#0F172A">
          <rect x="206" y="71" width="4" height="4" />
          <rect x="206" y="93" width="4" height="4" />
          <rect x="195" y="82" width="4" height="4" />
          <rect x="217" y="82" width="4" height="4" />
        </g>

        {/* Four leaf-shaped cutouts in the corners, kissing the center wheel */}
        <path d="M 184 60 Q 200 64 200 76 Q 188 78 184 70 Z" fill="#FFFFFF" />
        <path d="M 232 60 Q 216 64 216 76 Q 228 78 232 70 Z" fill="#FFFFFF" />
        <path d="M 184 110 Q 200 106 200 94 Q 188 92 184 102 Z" fill="#FFFFFF" />
        <path d="M 232 110 Q 216 106 216 94 Q 228 92 232 102 Z" fill="#FFFFFF" />

        {/* Two diamond cutouts at the bottom strip */}
        <polygon points="184,128 192,122 200,128 192,134" fill="#FFFFFF" />
        <polygon points="216,128 224,122 232,128 224,134" fill="#FFFFFF" />

        {/* Three small dots row at the very bottom — connected via thin
            visual line implied by the rounded plate corners */}
        <g fill="#FFFFFF">
          <circle cx="188" cy="144" r="2.5" />
          <circle cx="208" cy="144" r="2.5" />
          <circle cx="228" cy="144" r="2.5" />
        </g>

        {/* Top border decorative slit */}
        <rect x="186" y="54" width="44" height="2.5" rx="1.25" fill="#FFFFFF" />
      </g>

      {/* Bottom tagline */}
      <g fontFamily="JetBrains Mono, monospace" fontSize="7" fill={EDGE} opacity="0.7">
        <text x="130" y="180" textAnchor="middle">prompt → DXF</text>
      </g>
    </Shell>
  );
}
